import { Download, ExternalLink, Filter, Scale, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Plot } from "./components/Plot";
import { loadAppData, loadCaseCharges, loadCaseEventFlags, loadCaseEventHighlights, loadCaseSearchRows } from "./lib/data";
import { csvEscape, dollars, numberOrDash, percent, shortDate } from "./lib/format";
import type { AppData, CaseChargeRow, CaseEventFlagRow, CaseEventHighlight, CaseSearchRow, CountDatum, DistributionDatum, Filters, FinancialCaseRow, FinancialPreview, MonthlyDatum, SearchRow } from "./lib/types";

const flagLabels: Record<string, string> = {
  condition_dna_sample: "DNA sample",
  condition_domestic_violence_eval: "Domestic violence evaluation",
  condition_evaluation: "Evaluation",
  condition_fingerprints: "Fingerprints",
  condition_no_alcohol: "No alcohol",
  condition_no_contact: "No contact",
  condition_no_firearms: "No firearms",
  condition_no_new_crimes: "No new crimes",
  condition_supervised_probation: "Supervised probation",
  condition_treatment: "Treatment"
};

const eventFlagLabels: Record<string, string> = {
  is_judgment: "Judgment",
  is_plea: "Plea",
  is_revocation: "Revocation",
  is_warrant_any: "Warrant",
  is_hearing: "Hearing",
  is_motion: "Motion",
  is_order: "Order",
  is_probation_event: "Probation event",
  is_dismissal: "Dismissal",
  is_service_document: "Service document",
  is_bond_event: "Bond event"
};

const sentenceFilterLabels: Partial<Record<keyof Filters, string>> = {
  query: "Search",
  dateFrom: "Date from",
  dateTo: "Date to",
  sentenceType: "Sentence type",
  eventTitle: "Event title",
  judge: "Sentence judge",
  caseCounty: "Case county",
  caseInitialJudge: "Initial judicial officer",
  caseOffenseCategory: "Offense category",
  caseChargeLevel: "Charge level",
  caseRevocation: "Later revocation",
  caseWarrant: "Warrant activity",
  conditionFlag: "Condition flag",
  conditionTitle: "Condition title",
  conditionStatus: "Condition status",
  minConfinement: "Min confinement",
  minSuspended: "Min suspended",
  minCredit: "Min credit",
  minFirstServe: "Min first serve",
  minProbation: "Min probation",
  sort: "Sort"
};

const initialFilters: Filters = {
  query: "",
  dateFrom: "",
  dateTo: "",
  sentenceType: "",
  eventTitle: "",
  judge: "",
  caseCounty: "",
  caseInitialJudge: "",
  caseOffenseCategory: "",
  caseChargeLevel: "",
  caseRevocation: "",
  caseWarrant: "",
  conditionFlag: "",
  minConfinement: "",
  minSuspended: "",
  minCredit: "",
  minFirstServe: "",
  minProbation: "",
  conditionTitle: "",
  conditionStatus: "",
  sort: "date_desc"
};

type DashboardMode = "sentencing" | "cases";
type SearchMode = "sentence" | "case";
type EventSearchFilters = {
  q: string;
  eventFrom: string;
  eventTo: string;
  eventBucket: string;
  eventFlag: string;
};

type CaseFilters = {
  query: string;
  filedFrom: string;
  filedTo: string;
  county: string;
  judge: string;
  offenseCategory: string;
  chargeLevel: string;
  offenseFrom: string;
  offenseTo: string;
  outcome: string;
  eventFlag: string;
  revocation: string;
  warrant: string;
  pledGuilty: string;
  dismissed: string;
  trialSet: string;
  appointedCounsel: string;
  privateCounsel: string;
  defenseType: string;
  sort: string;
};

type FinancialFilters = {
  query: string;
  status: string;
  county: string;
  judge: string;
  offenseCategory: string;
  chargeLevel: string;
  revocation: string;
  minBalance: string;
  maxBalance: string;
  minAssessment: string;
  maxAssessment: string;
  minPayments: string;
  maxPayments: string;
  quality: string;
  sort: string;
};

const caseFilterLabels: Partial<Record<keyof CaseFilters, string>> = {
  query: "Search",
  filedFrom: "Filed from",
  filedTo: "Filed to",
  county: "County",
  judge: "Initial judicial officer",
  offenseCategory: "Offense category",
  chargeLevel: "Charge level",
  offenseFrom: "Offense from",
  offenseTo: "Offense to",
  outcome: "Outcome",
  eventFlag: "Procedural event",
  revocation: "Revocation activity",
  warrant: "Warrant activity",
  pledGuilty: "Pled guilty",
  dismissed: "Dismissed ever",
  trialSet: "Trial set",
  appointedCounsel: "Appointed counsel",
  privateCounsel: "Private counsel",
  defenseType: "Defense type",
  sort: "Sort"
};

const initialCaseFilters: CaseFilters = {
  query: "",
  filedFrom: "",
  filedTo: "",
  county: "",
  judge: "",
  offenseCategory: "",
  chargeLevel: "",
  offenseFrom: "",
  offenseTo: "",
  outcome: "",
  eventFlag: "",
  revocation: "",
  warrant: "",
  pledGuilty: "",
  dismissed: "",
  trialSet: "",
  appointedCounsel: "",
  privateCounsel: "",
  defenseType: "",
  sort: "filed_desc"
};

const initialFinancialFilters: FinancialFilters = {
  query: "",
  status: "",
  county: "",
  judge: "",
  offenseCategory: "",
  chargeLevel: "",
  revocation: "",
  minBalance: "",
  maxBalance: "",
  minAssessment: "",
  maxAssessment: "",
  minPayments: "",
  maxPayments: "",
  quality: "",
  sort: "balance_desc"
};

const initialEventSearch: EventSearchFilters = {
  q: "",
  eventFrom: "",
  eventTo: "",
  eventBucket: "",
  eventFlag: ""
};

const colors = {
  sentence: "#2563eb",
  case: "#0f766e",
  condition: "#7c3aed",
  judge: "#475569",
  procedure: "#b45309",
  financial: "#0f766e",
  warning: "#be123c",
  neutral: "#64748b",
  reference: "#94a3b8"
};

const eventBucketColors = ["#2563eb", "#0f766e", "#b45309", "#be123c", "#7c3aed", "#64748b", "#0891b2"];

const sentenceFilterKeys = Object.keys(initialFilters) as Array<keyof Filters>;
const caseFilterKeys = Object.keys(initialCaseFilters) as Array<keyof CaseFilters>;

function sentenceFiltersFromParams(params: URLSearchParams): Filters {
  const next = { ...initialFilters };
  for (const key of sentenceFilterKeys) {
    const value = params.get(key);
    if (value !== null) {
      next[key] = value;
    }
  }
  if (params.get("q")) next.query = params.get("q") ?? "";
  if (params.get("type")) next.sentenceType = params.get("type") ?? "";
  return next;
}

function caseFiltersFromParams(params: URLSearchParams): CaseFilters {
  const next = { ...initialCaseFilters };
  for (const key of caseFilterKeys) {
    const value = params.get(key);
    if (value !== null) {
      next[key] = value;
    }
  }
  if (params.get("q")) next.query = params.get("q") ?? "";
  return next;
}

function activeEntries<T extends Record<string, string>>(filters: T, initial: T) {
  return Object.entries(filters).filter(([key, value]) => value && value !== initial[key]);
}

function boolLabel(value: string) {
  if (value === "1") return "Yes";
  if (value === "0") return "No";
  return value;
}

function sentenceFilterValueLabel(key: string, value: string) {
  if (key === "conditionFlag") return flagLabels[value] ?? value;
  if (key === "caseRevocation" || key === "caseWarrant") return boolLabel(value);
  return labelize(value);
}

function caseFilterValueLabel(key: string, value: string) {
  if (key === "eventFlag") return eventFlagLabels[value] ?? value;
  if (["revocation", "warrant", "pledGuilty", "dismissed", "trialSet", "appointedCounsel", "privateCounsel"].includes(key)) return boolLabel(value);
  return labelize(value);
}

function rowMatches(row: SearchRow, filters: Filters) {
  const query = filters.query.trim().toLowerCase();
  const haystack = [
    row.case_number,
    row.source_file,
    row.agency,
    row.sentence_event_title,
    row.sentence_type,
    row.judge_names,
    row.case_county,
    row.case_judicial_officer_initial,
    row.case_primary_charge_level,
    row.case_primary_offense_category,
    row.condition_titles,
    row.condition_statuses
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (query && !haystack.includes(query)) return false;
  if (filters.dateFrom && (!row.sentence_event_date || row.sentence_event_date < filters.dateFrom)) return false;
  if (filters.dateTo && (!row.sentence_event_date || row.sentence_event_date > filters.dateTo)) return false;
  if (filters.sentenceType && row.sentence_type !== filters.sentenceType) return false;
  if (filters.eventTitle && row.sentence_event_title !== filters.eventTitle) return false;
  if (filters.judge && !(row.judge_names ?? "").split(",").includes(filters.judge)) return false;
  if (filters.caseCounty && row.case_county !== filters.caseCounty) return false;
  if (filters.caseInitialJudge && row.case_judicial_officer_initial !== filters.caseInitialJudge) return false;
  if (filters.caseOffenseCategory && row.case_primary_offense_category !== filters.caseOffenseCategory) return false;
  if (filters.caseChargeLevel && row.case_primary_charge_level !== filters.caseChargeLevel) return false;
  if (filters.caseRevocation && row.case_revocation_ever !== Number(filters.caseRevocation)) return false;
  if (filters.caseWarrant && row.case_warrant_any_ever !== Number(filters.caseWarrant)) return false;
  if (filters.conditionFlag && Number(row[filters.conditionFlag as keyof SearchRow]) !== 1) return false;
  if (filters.conditionTitle && !(row.condition_titles ?? "").split(",").includes(filters.conditionTitle)) return false;
  if (filters.conditionStatus && !(row.condition_statuses ?? "").split(",").includes(filters.conditionStatus)) return false;
  if (filters.minConfinement && (row.confinement_months_imposed ?? -Infinity) < Number(filters.minConfinement)) return false;
  if (filters.minSuspended && (row.confinement_months_suspended ?? -Infinity) < Number(filters.minSuspended)) return false;
  if (filters.minCredit && (row.credit_for_time_served_days ?? -Infinity) < Number(filters.minCredit)) return false;
  if (filters.minFirstServe && (row.first_serve_days ?? -Infinity) < Number(filters.minFirstServe)) return false;
  if (filters.minProbation && (row.probation_months ?? -Infinity) < Number(filters.minProbation)) return false;
  return true;
}

function sortRows(rows: SearchRow[], sort: string) {
  return [...rows].sort((a, b) => {
    if (sort === "case") return a.case_number.localeCompare(b.case_number);
    if (sort === "judge") return (a.judge_names ?? "").localeCompare(b.judge_names ?? "");
    if (sort === "confinement") return (b.confinement_months_imposed ?? -1) - (a.confinement_months_imposed ?? -1);
    if (sort === "probation") return (b.probation_months ?? -1) - (a.probation_months ?? -1);
    if (sort === "conditions") return b.condition_count - a.condition_count;
    return (b.sentence_event_date ?? "").localeCompare(a.sentence_event_date ?? "");
  });
}

function linearTrend(rows: MonthlyDatum[], selector: (row: MonthlyDatum) => number | null): Array<number | null> {
  const points = rows
    .map((row, index) => ({ x: index, y: selector(row) }))
    .filter((point): point is { x: number; y: number } => point.y !== null && !Number.isNaN(point.y));

  if (points.length < 2) {
    return rows.map(() => null);
  }

  const n = points.length;
  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const sumXY = points.reduce((sum, point) => sum + point.x * point.y, 0);
  const sumXX = points.reduce((sum, point) => sum + point.x * point.x, 0);
  const denominator = n * sumXX - sumX * sumX;

  if (denominator === 0) {
    return rows.map(() => null);
  }

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;
  return rows.map((_, index) => Number((intercept + slope * index).toFixed(2)));
}

const sentenceLengthBins = [
  { label: "0 months", min: 0, max: 0 },
  { label: "1-6 months", min: 1, max: 6 },
  { label: "7-12 months", min: 7, max: 12 },
  { label: "13-18 months", min: 13, max: 18 },
  { label: "19-24 months", min: 19, max: 24 },
  { label: "25-36 months", min: 25, max: 36 },
  { label: "37-60 months", min: 37, max: 60 },
  { label: "61-120 months", min: 61, max: 120 },
  { label: "More than 120 months", min: 121, max: Infinity }
];

function withPercent(rows: CountDatum[], total: number) {
  return rows.map((row) => ({
    ...row,
    percent: total ? Number(((row.value / total) * 100).toFixed(1)) : 0
  }));
}

function binSentenceLengths(rows: DistributionDatum[]) {
  const total = rows.reduce((sum, row) => sum + row.events, 0);
  return sentenceLengthBins.map((bin) => {
    const events = rows.reduce((sum, row) => {
      if (row.months >= bin.min && row.months <= bin.max) {
        return sum + row.events;
      }
      return sum;
    }, 0);
    return {
      label: bin.label,
      events,
      percent: total ? Number(((events / total) * 100).toFixed(1)) : 0
    };
  });
}

function displayPercent(value: number) {
  return `${value.toFixed(1)}%`;
}

function labelize(value: string | null | undefined) {
  if (!value) return "Unavailable";
  return value
    .split("_")
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : part)
    .join(" ");
}

function compactNumber(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) return "Unavailable";
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

function sumValues<T>(rows: T[], selector: (row: T) => number | null | undefined) {
  return rows.reduce((sum, row) => sum + (selector(row) ?? 0), 0);
}

function percentOf(part: number | null | undefined, total: number | null | undefined) {
  return total ? Number((((part ?? 0) / total) * 100).toFixed(1)) : 0;
}

function endpointText<T>(rows: T[], value: (row: T) => number | null | undefined, label: string) {
  const first = rows.find((row) => value(row) !== null && value(row) !== undefined);
  const last = [...rows].reverse().find((row) => value(row) !== null && value(row) !== undefined);
  if (!first || !last) return `${label}: unavailable`;
  return `${label}: ${compactNumber(value(first))} to ${compactNumber(value(last))}`;
}

function byFiledYear(rows: CaseSearchRow[]) {
  const groups = new Map<string, { year: string; cases: number; guilty: number; revocation: number; warrant: number; judgmentDays: number[]; dispositionDays: number[] }>();
  for (const row of rows) {
    const year = row.filed_date?.slice(0, 4);
    if (!year) continue;
    const bucket = groups.get(year) ?? { year, cases: 0, guilty: 0, revocation: 0, warrant: 0, judgmentDays: [], dispositionDays: [] };
    bucket.cases += 1;
    bucket.guilty += row.pled_guilty_ever === 1 ? 1 : 0;
    bucket.revocation += row.revocation_ever === 1 ? 1 : 0;
    bucket.warrant += row.warrant_any_ever === 1 ? 1 : 0;
    if (row.days_file_to_first_judgment !== null) bucket.judgmentDays.push(row.days_file_to_first_judgment);
    if (row.days_file_to_final_disposition !== null) bucket.dispositionDays.push(row.days_file_to_final_disposition);
    groups.set(year, bucket);
  }
  return [...groups.values()].sort((a, b) => a.year.localeCompare(b.year)).map((row) => ({
    ...row,
    guiltyShare: percentOf(row.guilty, row.cases),
    revocationShare: percentOf(row.revocation, row.cases),
    warrantShare: percentOf(row.warrant, row.cases),
    medianJudgmentDays: median(row.judgmentDays),
    medianDispositionDays: median(row.dispositionDays)
  }));
}

function median(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Number(((sorted[mid - 1] + sorted[mid]) / 2).toFixed(1));
}

function rateRows<T extends { month: string }>(
  rows: T[],
  filedRows: Array<{ month: string; cases: number }>,
  selector: (row: T) => number | null | undefined,
  outputKey: string
) {
  return rows.map((row) => {
    const cases = filedRows.find((filed) => filed.month === row.month)?.cases ?? null;
    return { month: row.month, [outputKey]: cases ? Number((((selector(row) ?? 0) / cases) * 100).toFixed(1)) : null, cases };
  });
}

function yesNo(value: number | null | undefined) {
  return value === 1 ? "Yes" : "No";
}

function conditionFlagLabel(flag: string) {
  return flagLabels[`condition_${flag}`] ?? labelize(flag);
}

function caseRowMatches(row: CaseSearchRow, filters: CaseFilters) {
  const query = filters.query.trim().toLowerCase();
  const haystack = [
    row.case_number,
    row.source_file,
    row.county,
    row.judicial_officer_initial,
    row.outcome_bucket,
    row.primary_charge_statute,
    row.primary_charge_description,
    row.primary_charge_level,
    row.primary_offense_category,
    row.defense_attorney_type_initial
  ].filter(Boolean).join(" ").toLowerCase();

  if (query && !haystack.includes(query)) return false;
  if (filters.filedFrom && (!row.filed_date || row.filed_date < filters.filedFrom)) return false;
  if (filters.filedTo && (!row.filed_date || row.filed_date > filters.filedTo)) return false;
  if (filters.offenseFrom && (!row.primary_charge_date || row.primary_charge_date < filters.offenseFrom)) return false;
  if (filters.offenseTo && (!row.primary_charge_date || row.primary_charge_date > filters.offenseTo)) return false;
  if (filters.county && row.county !== filters.county) return false;
  if (filters.judge && row.judicial_officer_initial !== filters.judge) return false;
  if (filters.offenseCategory && row.primary_offense_category !== filters.offenseCategory) return false;
  if (filters.chargeLevel && row.primary_charge_level !== filters.chargeLevel) return false;
  if (filters.outcome && row.outcome_bucket !== filters.outcome) return false;
  if (filters.revocation && row.revocation_ever !== Number(filters.revocation)) return false;
  if (filters.warrant && row.warrant_any_ever !== Number(filters.warrant)) return false;
  if (filters.pledGuilty && row.pled_guilty_ever !== Number(filters.pledGuilty)) return false;
  if (filters.dismissed && row.dismissed_ever !== Number(filters.dismissed)) return false;
  if (filters.trialSet && row.trial_set_ever !== Number(filters.trialSet)) return false;
  if (filters.appointedCounsel && row.appointed_counsel_ever !== Number(filters.appointedCounsel)) return false;
  if (filters.privateCounsel && row.private_counsel_ever !== Number(filters.privateCounsel)) return false;
  if (filters.defenseType && row.defense_attorney_type_initial !== filters.defenseType) return false;
  return true;
}

function sortCaseRows(rows: CaseSearchRow[], sort: string) {
  return [...rows].sort((a, b) => {
    if (sort === "case") return a.case_number.localeCompare(b.case_number);
    if (sort === "judge") return (a.judicial_officer_initial ?? "").localeCompare(b.judicial_officer_initial ?? "");
    if (sort === "charges") return (b.charge_count ?? -1) - (a.charge_count ?? -1);
    if (sort === "events") return (b.event_count ?? -1) - (a.event_count ?? -1);
    if (sort === "judgment_days") return (b.days_file_to_first_judgment ?? -1) - (a.days_file_to_first_judgment ?? -1);
    return (b.filed_date ?? "").localeCompare(a.filed_date ?? "");
  });
}

function reconciliationIssue(row: FinancialCaseRow) {
  return row.reconciliation_difference !== null && Math.abs(row.reconciliation_difference) > 0.01;
}

function financialRowMatches(row: FinancialCaseRow, filters: FinancialFilters) {
  const query = filters.query.trim().toLowerCase();
  const haystack = [
    row.case_number,
    row.county,
    row.judicial_officer_initial,
    row.outcome_bucket,
    row.primary_charge_level,
    row.primary_offense_category,
    row.balance_status
  ].filter(Boolean).join(" ").toLowerCase();

  if (query && !haystack.includes(query)) return false;
  if (filters.status && row.balance_status !== filters.status) return false;
  if (filters.county && row.county !== filters.county) return false;
  if (filters.judge && row.judicial_officer_initial !== filters.judge) return false;
  if (filters.offenseCategory && row.primary_offense_category !== filters.offenseCategory) return false;
  if (filters.chargeLevel && row.primary_charge_level !== filters.chargeLevel) return false;
  if (filters.revocation && row.revocation_ever !== Number(filters.revocation)) return false;
  if (filters.minBalance && (row.balance_due ?? -Infinity) < Number(filters.minBalance)) return false;
  if (filters.maxBalance && (row.balance_due ?? Infinity) > Number(filters.maxBalance)) return false;
  if (filters.minAssessment && (row.total_financial_assessment ?? -Infinity) < Number(filters.minAssessment)) return false;
  if (filters.maxAssessment && (row.total_financial_assessment ?? Infinity) > Number(filters.maxAssessment)) return false;
  if (filters.minPayments && (row.total_payments_and_credits ?? -Infinity) < Number(filters.minPayments)) return false;
  if (filters.maxPayments && (row.total_payments_and_credits ?? Infinity) > Number(filters.maxPayments)) return false;
  if (filters.quality === "discrepancy" && !reconciliationIssue(row)) return false;
  if (filters.quality === "payments_exceed" && !((row.total_payments_and_credits ?? -Infinity) > (row.total_financial_assessment ?? Infinity))) return false;
  if (filters.quality === "high_balance" && !((row.balance_due ?? 0) >= 25000)) return false;
  return true;
}

function sortFinancialRows(rows: FinancialCaseRow[], sort: string) {
  return [...rows].sort((a, b) => {
    if (sort === "assessment_desc") return (b.total_financial_assessment ?? -1) - (a.total_financial_assessment ?? -1);
    if (sort === "payments_desc") return (b.total_payments_and_credits ?? -1) - (a.total_payments_and_credits ?? -1);
    if (sort === "case") return a.case_number.localeCompare(b.case_number);
    if (sort === "judge") return (a.judicial_officer_initial ?? "").localeCompare(b.judicial_officer_initial ?? "");
    if (sort === "filed_desc") return (b.filed_date ?? "").localeCompare(a.filed_date ?? "");
    return (b.balance_due ?? -1) - (a.balance_due ?? -1);
  });
}

function balanceStatusLabel(status: string | null | undefined) {
  if (status === "paid_in_full") return "Paid in full / zero balance";
  if (status === "open_balance") return "Open balance";
  if (status === "review") return "Review";
  return "Unavailable";
}

function exportCaseCsv(rows: CaseSearchRow[]) {
  const columns = [
    "case_number",
    "filed_date",
    "county",
    "judicial_officer_initial",
    "outcome_bucket",
    "primary_charge_level",
    "primary_offense_category",
    "charge_count",
    "event_count",
    "revocation_ever",
    "warrant_any_ever",
    "source_file"
  ];
  const csv = [
    columns.join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column as keyof CaseSearchRow])).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "filtered-case-profiles.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function exportFinancialCsv(rows: FinancialCaseRow[]) {
  const columns = [
    "case_number",
    "filed_date",
    "county",
    "judicial_officer_initial",
    "outcome_bucket",
    "primary_charge_level",
    "primary_offense_category",
    "revocation_ever",
    "balance_due",
    "total_financial_assessment",
    "total_payments_and_credits",
    "balance_status",
    "reconciliation_difference"
  ];
  const csv = [
    columns.join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column as keyof FinancialCaseRow])).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "attorney-preview-financial-case-profiles.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function parseNaturalQuery(filters: Filters): Filters {
  const query = filters.query.toLowerCase();
  const next = { ...filters };
  let recognized = false;
  const yearMatch = query.match(/\b(?:since|after)\s+(20\d{2})\b/);
  const beforeMatch = query.match(/\bbefore\s+(20\d{2})\b/);

  if (/\brevocations?\b/.test(query)) { next.sentenceType = "revocation"; recognized = true; }
  if (/\boriginals?\b/.test(query)) { next.sentenceType = "original"; recognized = true; }
  if (yearMatch) { next.dateFrom = `${yearMatch[1]}-01-01`; recognized = true; }
  if (beforeMatch) { next.dateTo = `${beforeMatch[1]}-12-31`; recognized = true; }
  if (/\btreatment\b/.test(query)) { next.conditionFlag = "condition_treatment"; recognized = true; }
  if (/\bfingerprints?\b/.test(query)) { next.conditionFlag = "condition_fingerprints"; recognized = true; }
  if (/\bdna\b/.test(query)) { next.conditionFlag = "condition_dna_sample"; recognized = true; }
  if (/\bno contact\b/.test(query)) { next.conditionFlag = "condition_no_contact"; recognized = true; }
  if (/\bprobation\b/.test(query)) { next.conditionFlag = "condition_supervised_probation"; recognized = true; }
  if (/\bactive\b/.test(query)) { next.conditionStatus = "active"; recognized = true; }
  if (/\bsatisfied\b/.test(query)) { next.conditionStatus = "satisfied"; recognized = true; }
  if (recognized) next.query = "";

  return next;
}

function exportCsv(rows: SearchRow[]) {
  const columns = [
    "sentence_event_id",
    "case_number",
    "sentence_event_date",
    "sentence_event_title",
    "sentence_type",
    "confinement_months_imposed",
    "confinement_months_suspended",
    "credit_for_time_served_days",
    "first_serve_days",
    "probation_months",
    "source_file"
  ];
  const csv = [
    columns.join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column as keyof SearchRow])).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "filtered-sentence-events.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function exportRecordsCsv(rows: Array<Record<string, unknown>>, filename: string) {
  if (!rows.length) return;
  const columns = Object.keys(rows[0]);
  const csv = [
    columns.join(","),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column])).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function Sparkline({ values, color = colors.sentence, label }: { values: Array<number | null | undefined>; color?: string; label: string }) {
  const clean = values.filter((value): value is number => value !== null && value !== undefined && !Number.isNaN(value));
  if (clean.length < 2) return null;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const width = 120;
  const height = 28;
  const points = values.map((value, index) => {
    if (value === null || value === undefined || Number.isNaN(value)) return null;
    const x = values.length === 1 ? 0 : (index / (values.length - 1)) * width;
    const y = max === min ? height / 2 : height - ((value - min) / (max - min)) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).filter(Boolean).join(" ");
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function MetricCard({ label, value, note, sparkline }: { label: string; value: string; note?: string; sparkline?: React.ReactNode }) {
  return (
    <div className="metric-card">
      <span>{label}</span>
      <strong>{value}</strong>
      {sparkline}
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function CompactMetricStrip({ children }: { children: React.ReactNode }) {
  return <section className="metric-strip">{children}</section>;
}

function ChartEvidence({ unit, denominator, source, caveat }: { unit: string; denominator?: string; source?: string; caveat?: string }) {
  return (
    <p className="evidence-note">
      Counts: {unit}.{denominator ? ` Denominator: ${denominator}.` : ""}{source ? ` Source: ${source}.` : ""}{caveat ? ` Caveat: ${caveat.replace(/\.$/, "")}.` : ""}
    </p>
  );
}

function InlineBar({ value, max, color = colors.sentence }: { value: number | null | undefined; max: number; color?: string }) {
  const width = max ? Math.max(2, ((value ?? 0) / max) * 100) : 0;
  return (
    <span className="inline-bar-cell">
      <span className="inline-bar-track"><span className="inline-bar-fill" style={{ width: `${width}%`, backgroundColor: color }} /></span>
      <span>{typeof value === "number" ? value.toLocaleString() : "Unavailable"}</span>
    </span>
  );
}

function RangeRow({ label, value, baseline, max = 100, color = colors.sentence }: { label: string; value: number | null | undefined; baseline?: number | null; max?: number; color?: string }) {
  const safeValue = Math.max(0, Math.min(max, value ?? 0));
  const safeBaseline = baseline === null || baseline === undefined ? null : Math.max(0, Math.min(max, baseline));
  return (
    <div className="range-row">
      <span>{label}: {value === null || value === undefined ? "Unavailable" : `${value.toFixed(1)}%`}{safeBaseline !== null ? ` | district ${safeBaseline.toFixed(1)}%` : ""}</span>
      <span className="range-track">
        <span className="range-fill" style={{ width: `${(safeValue / max) * 100}%`, backgroundColor: color }} />
        {safeBaseline !== null ? <span className="range-marker" style={{ left: `${(safeBaseline / max) * 100}%` }} /> : null}
      </span>
    </div>
  );
}

function DenseComparisonTable({ rows, labelKey, valueKey, percentKey, color = colors.sentence }: { rows: Array<Record<string, unknown>>; labelKey: string; valueKey: string; percentKey?: string; color?: string }) {
  const [sortKey, setSortKey] = useState(valueKey);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const sortedRows = [...rows].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    const numericA = Number(aValue);
    const numericB = Number(bValue);
    const comparison = Number.isFinite(numericA) && Number.isFinite(numericB)
      ? numericA - numericB
      : String(aValue ?? "").localeCompare(String(bValue ?? ""));
    return sortDirection === "asc" ? comparison : -comparison;
  });
  const max = Math.max(...rows.map((row) => Number(row[valueKey]) || 0), 1);
  function sortBy(key: string) {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection(key === labelKey ? "asc" : "desc");
    }
  }
  return (
    <div className="table-wrap compact">
      <button className="secondary table-export" onClick={() => exportRecordsCsv(rows, `${labelKey.replace(/\s+/g, "-").toLowerCase()}-comparison.csv`)}>Export chart CSV</button>
      <table>
        <thead><tr><th><button className="table-sort" onClick={() => sortBy(labelKey)}>{labelKey}</button></th><th><button className="table-sort" onClick={() => sortBy(valueKey)}>{valueKey}</button></th>{percentKey ? <th><button className="table-sort" onClick={() => sortBy(percentKey)}>{percentKey}</button></th> : null}</tr></thead>
        <tbody>
          {sortedRows.map((row, index) => (
            <tr key={index}>
              <td>{String(row[labelKey] ?? "Unavailable")}</td>
              <td><InlineBar value={Number(row[valueKey]) || 0} max={max} color={color} /></td>
              {percentKey ? <td>{String(row[percentKey] ?? "Unavailable")}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TrendRowsTable({ rows }: { rows: Array<{ label: string; values: number[]; total: number; color: string }> }) {
  const max = Math.max(...rows.map((row) => row.total), 1);
  return (
    <div className="table-wrap compact">
      <button className="secondary table-export" onClick={() => exportRecordsCsv(rows.map((row) => ({ bucket: row.label, total: row.total })), "trend-sparklines.csv")}>Export chart CSV</button>
      <table>
        <thead><tr><th>bucket</th><th>trend</th><th>events</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td>{row.label}</td>
              <td><Sparkline values={row.values} color={row.color} label={`${row.label} trend`} /></td>
              <td><InlineBar value={row.total} max={max} color={row.color} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChartTable({ rows, columns, barColumn, color = colors.sentence, filename = "chart-data.csv" }: { rows: Array<Record<string, unknown>>; columns?: string[]; barColumn?: string; color?: string; filename?: string }) {
  if (!rows.length) {
    return <p className="chart-note">No tabular chart data available.</p>;
  }
  const displayColumns = columns ?? Object.keys(rows[0]).slice(0, 6);
  const max = barColumn ? Math.max(...rows.map((row) => Number(row[barColumn]) || 0), 1) : 1;
  return (
    <details className="chart-table">
      <summary>Chart data table</summary>
      <button className="secondary" onClick={() => exportRecordsCsv(rows, filename)}>Export chart CSV</button>
      <table>
        <thead>
          <tr>{displayColumns.map((column) => <th key={column}>{column}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 12).map((row, index) => (
            <tr key={index}>{displayColumns.map((column) => <td key={column}>{column === barColumn ? <InlineBar value={Number(row[column]) || 0} max={max} color={color} /> : String(row[column] ?? "Unavailable")}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

type ConditionDisplay = "events" | "cases";

function conditionMetricValue(row: CountDatum, display: ConditionDisplay) {
  return display === "cases" ? row.casePercent ?? 0 : row.value;
}

function conditionMetricLabel(display: ConditionDisplay) {
  return display === "cases" ? "% of cases" : "Sentence events";
}

function conditionRowsForDisplay(rows: CountDatum[], display: ConditionDisplay) {
  return rows.map((row) => ({ ...row, metricValue: conditionMetricValue(row, display) }));
}

function conditionTableRows(rows: CountDatum[]) {
  return rows.map((row) => ({
    condition: row.label,
    "sentence events": row.value,
    "cases with condition": row.caseCount,
    "% of cases": row.casePercent === undefined ? undefined : `${row.casePercent.toFixed(2)}%`
  }));
}

function formatConditionMetric(value: number, display: ConditionDisplay) {
  return display === "cases" ? `${value.toFixed(1)}%` : value.toLocaleString();
}

function ConditionTreemap({ rows, display }: { rows: Array<CountDatum & { metricValue: number }>; display: ConditionDisplay }) {
  const max = Math.max(...rows.map((row) => row.metricValue), 1);
  const metricLabel = conditionMetricLabel(display);
  return (
    <div className="treemap" role="img" aria-label={`Condition treemap sized by ${metricLabel.toLowerCase()}`}>
      {rows.slice(0, 18).map((row, index) => (
        <button
          key={row.label}
          className="treemap-tile"
          style={{
            flexBasis: `${Math.max(18, (row.metricValue / max) * 58)}%`,
            backgroundColor: `hsl(217 78% ${Math.max(34, 68 - index * 1.4)}%)`
          }}
          title={`${row.label}: ${formatConditionMetric(row.metricValue, display)} ${metricLabel.toLowerCase()}`}
        >
          <strong>{row.label}</strong>
          <span>{formatConditionMetric(row.metricValue, display)}</span>
        </button>
      ))}
    </div>
  );
}

export function App() {
  const [data, setData] = useState<AppData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") ?? "dashboard");
  const [dashboardMode, setDashboardMode] = useState<DashboardMode>("sentencing");
  const [searchMode, setSearchMode] = useState<SearchMode>(() => new URLSearchParams(window.location.search).get("mode") === "cases" ? "case" : "sentence");
  const [conditionDisplay, setConditionDisplay] = useState<ConditionDisplay>("events");
  const [caseDetail, setCaseDetail] = useState<Record<string, unknown> | null>(null);
  const [caseProfileDetail, setCaseProfileDetail] = useState<{ profile: CaseSearchRow; charges: CaseChargeRow[]; events: CaseEventHighlight[]; quality: Array<Record<string, unknown>>; financial?: FinancialCaseRow } | null>(null);
  const [eventDetail, setEventDetail] = useState<Record<string, unknown> | null>(null);
  const [caseRows, setCaseRows] = useState<CaseSearchRow[] | null>(null);
  const [caseCharges, setCaseCharges] = useState<CaseChargeRow[] | null>(null);
  const [caseEventHighlights, setCaseEventHighlights] = useState<CaseEventHighlight[] | null>(null);
  const [caseEventFlags, setCaseEventFlags] = useState<CaseEventFlagRow[] | null>(null);
  const [caseDataLoading, setCaseDataLoading] = useState(false);
  const [selectedJudgeName, setSelectedJudgeName] = useState("");
  const [eventSearchFilters, setEventSearchFilters] = useState<EventSearchFilters>(initialEventSearch);
  const [eventSearchPayload, setEventSearchPayload] = useState<{ results: Array<Record<string, unknown>>; meta: Record<string, unknown> } | null>(null);
  const [eventSearchLoading, setEventSearchLoading] = useState(false);
  const [qualityIssue, setQualityIssue] = useState("");
  const [qualityAffectedPayload, setQualityAffectedPayload] = useState<{ affectedCases: Array<Record<string, unknown>>; meta: Record<string, unknown> } | null>(null);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState<Array<{ name: string; filters: Filters }>>(() => {
    try {
      return JSON.parse(localStorage.getItem("scjd-saved-comparisons") ?? "[]");
    } catch {
      return [];
    }
  });
  const [filters, setFilters] = useState<Filters>(() => {
    const params = new URLSearchParams(window.location.search);
    return sentenceFiltersFromParams(params);
  });
  const [caseFilters, setCaseFilters] = useState<CaseFilters>(() => {
    const params = new URLSearchParams(window.location.search);
    return caseFiltersFromParams(params);
  });
  const [financialFilters, setFinancialFilters] = useState<FinancialFilters>(initialFinancialFilters);

  useEffect(() => {
    loadAppData().then(setData).catch((err) => setError(String(err)));
  }, []);

  useEffect(() => {
    if (!["case"].includes(searchMode) && !["dashboard", "trends"].includes(activeTab)) return;
    if (caseRows) return;
    setCaseDataLoading(true);
    loadCaseSearchRows()
      .then(setCaseRows)
      .catch((err) => setError(String(err)))
      .finally(() => setCaseDataLoading(false));
  }, [activeTab, caseRows, searchMode]);

  useEffect(() => {
    if (searchMode !== "case" || !caseFilters.eventFlag || caseEventFlags) return;
    loadCaseEventFlags().then(setCaseEventFlags).catch((err) => setError(String(err)));
  }, [caseEventFlags, caseFilters.eventFlag, searchMode]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    return sortRows(data.search.filter((row) => rowMatches(row, filters)), filters.sort);
  }, [data, filters]);

  const filteredCaseRows = useMemo(() => {
    if (!caseRows) return [];
    const caseNumbersWithFlag = caseFilters.eventFlag && caseEventFlags
      ? new Set(
        caseEventFlags
          .filter((row) => Number(row[caseFilters.eventFlag as keyof CaseEventFlagRow]) === 1)
          .map((row) => row.case_number)
      )
      : null;
    return sortCaseRows(
      caseRows.filter((row) => caseRowMatches(row, caseFilters) && (!caseNumbersWithFlag || caseNumbersWithFlag.has(row.case_number))),
      caseFilters.sort
    );
  }, [caseEventFlags, caseRows, caseFilters]);
  const filteredFinancialRows = useMemo(() => {
    if (!data) return [];
    return sortFinancialRows(data.financialPreview.cases.filter((row) => financialRowMatches(row, financialFilters)), financialFilters.sort);
  }, [data, financialFilters]);

  const sentenceTypes = useMemo(() => [...new Set(data?.search.map((row) => row.sentence_type).filter(Boolean))], [data]);
  const eventTitles = useMemo(() => [...new Set(data?.search.map((row) => row.sentence_event_title).filter(Boolean))].slice(0, 80), [data]);
  const judgeNames = useMemo(() => [...new Set(data?.search.flatMap((row) => (row.judge_names ?? "").split(",").filter(Boolean)))].sort(), [data]);
  const conditionTitles = useMemo(() => [...new Set(data?.search.flatMap((row) => (row.condition_titles ?? "").split(",").filter(Boolean)))].sort().slice(0, 120), [data]);
  const conditionStatuses = useMemo(() => [...new Set(data?.search.flatMap((row) => (row.condition_statuses ?? "").split(",").filter(Boolean)))].sort(), [data]);
  const conditionTitleRows = useMemo(() => conditionRowsForDisplay(data?.charts.conditionTitles ?? [], conditionDisplay), [data, conditionDisplay]);
  const conditionFlagRows = useMemo(() => conditionRowsForDisplay(data?.charts.conditionFlags ?? [], conditionDisplay), [data, conditionDisplay]);
  const avgConfinementTrend = useMemo(() => linearTrend(data?.charts.monthlyEvents ?? [], (row) => row.avgConfinement), [data]);
  const avgProbationTrend = useMemo(() => linearTrend(data?.charts.monthlyEvents ?? [], (row) => row.avgProbation), [data]);
  const sentenceTypeShare = useMemo(() => withPercent(data?.charts.sentenceTypes ?? [], data?.summary.totalSentenceEvents ?? 0), [data]);
  const confinementBins = useMemo(() => binSentenceLengths(data?.charts.confinementDistribution ?? []).filter((row) => row.events > 0), [data]);
  const probationBins = useMemo(() => binSentenceLengths(data?.charts.probationDistribution ?? []).filter((row) => row.events > 0), [data]);
  const commonEventTitles = useMemo(() => (data?.charts.eventTitles ?? []).slice(0, 8), [data]);
  const sentenceCaseCounties = useMemo(() => [...new Set(data?.search.map((row) => row.case_county).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const sentenceCaseJudges = useMemo(() => [...new Set(data?.search.map((row) => row.case_judicial_officer_initial).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const sentenceCaseCategories = useMemo(() => [...new Set(data?.search.map((row) => row.case_primary_offense_category).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const sentenceCaseLevels = useMemo(() => [...new Set(data?.search.map((row) => row.case_primary_charge_level).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const caseCounties = useMemo(() => [...new Set(caseRows?.map((row) => row.county).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const caseJudgeNames = useMemo(() => [...new Set(caseRows?.map((row) => row.judicial_officer_initial).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const caseOffenseCategories = useMemo(() => [...new Set(caseRows?.map((row) => row.primary_offense_category).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const caseChargeLevels = useMemo(() => [...new Set(caseRows?.map((row) => row.primary_charge_level).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const caseOutcomes = useMemo(() => [...new Set(caseRows?.map((row) => row.outcome_bucket).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const caseDefenseTypes = useMemo(() => [...new Set(caseRows?.map((row) => row.defense_attorney_type_initial).filter((value): value is string => Boolean(value)))].sort(), [caseRows]);
  const financialCounties = useMemo(() => [...new Set(data?.financialPreview.cases.map((row) => row.county).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const financialJudges = useMemo(() => [...new Set(data?.financialPreview.cases.map((row) => row.judicial_officer_initial).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const financialCategories = useMemo(() => [...new Set(data?.financialPreview.cases.map((row) => row.primary_offense_category).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const financialLevels = useMemo(() => [...new Set(data?.financialPreview.cases.map((row) => row.primary_charge_level).filter((value): value is string => Boolean(value)))].sort(), [data]);
  const chartLevels = useMemo(() => [...new Set(data?.caseContextCharts.monthlyChargeLevels.map((row) => row.level) ?? [])].slice(0, 8), [data]);
  const eventBuckets = useMemo(() => [...new Set(data?.caseContextCharts.monthlyEventBuckets.map((row) => row.bucket) ?? [])].slice(0, 7), [data]);
  const selectedJudge = selectedJudgeName || data?.caseJudges[0]?.judge_name || "";
  const caseCohorts = useMemo(() => byFiledYear(caseRows ?? []), [caseRows]);
  const warrantRates = useMemo(() => rateRows(data?.caseContextCharts.monthlyWarrants ?? [], data?.caseContextCharts.monthlyFiled ?? [], (row) => row.warrants, "warrantsPer100Filed"), [data]);
  const proceduralRates = useMemo(() => rateRows(data?.caseContextCharts.monthlyEvents ?? [], data?.caseContextCharts.monthlyFiled ?? [], (row) => row.events, "eventsPerFiledCase"), [data]);
  const revocationRates = useMemo(() => rateRows(data?.caseContextCharts.monthlyRevocationEvents ?? [], data?.caseContextCharts.monthlyFiled ?? [], (row) => row.revocationEvents, "revocationsPer100Filed"), [data]);
  const dashboardOffenseRows = useMemo(() => (data?.caseContextCharts.sentencingByOffenseCategory ?? []).map((row) => ({
    category: labelize(row.category),
    events: row.sentenceEvents,
    confinement: row.avgConfinementMonths,
    probation: row.avgProbationMonths,
    revocation: row.revocationShare
  })), [data]);
  const lifecycleRows = useMemo(() => {
    if (!data) return [];
    const cases = data.caseContextSummary.uniqueCaseNumbers;
    const guilty = Math.round(cases * ((data.caseContextSummary.guiltyPleaCaseShare ?? 0) / 100));
    const judgment = data.caseContextSummary.medianDaysToJudgment === null ? 0 : cases;
    const revocation = Math.round(cases * ((data.caseContextSummary.revocationCaseShare ?? 0) / 100));
    const finalDisposition = data.caseContextSummary.medianDaysToFinalDisposition === null ? 0 : cases;
    return [
      { stage: "Filed", cases, days: 0 },
      { stage: "First plea", cases: guilty, days: data.caseContextSummary.medianDaysToPlea },
      { stage: "First judgment", cases: judgment, days: data.caseContextSummary.medianDaysToJudgment },
      { stage: "First revocation", cases: revocation, days: data.caseContextSummary.medianDaysJudgmentToRevocation },
      { stage: "Final disposition", cases: finalDisposition, days: data.caseContextSummary.medianDaysToFinalDisposition }
    ];
  }, [data]);
  const selectedJudgeSummary = useMemo(() => data?.caseJudges.find((judge) => judge.judge_name === selectedJudge), [data, selectedJudge]);
  const districtRevocationShare = data?.caseContextSummary.revocationCaseShare ?? null;
  const districtWarrantShare = data?.caseContextSummary.warrantCaseShare ?? null;

  function updateFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    const next = { ...filters, [key]: value };
    applyFilters(next);
  }

  function updateCaseFilter<K extends keyof CaseFilters>(key: K, value: CaseFilters[K]) {
    const next = { ...caseFilters, [key]: value };
    applyCaseFilters(next);
  }

  function updateFinancialFilter<K extends keyof FinancialFilters>(key: K, value: FinancialFilters[K]) {
    setFinancialFilters({ ...financialFilters, [key]: value });
  }

  function updateEventSearch<K extends keyof EventSearchFilters>(key: K, value: EventSearchFilters[K]) {
    setEventSearchFilters({ ...eventSearchFilters, [key]: value });
  }

  function applyFilters(next: Filters) {
    setFilters(next);
    const params = new URLSearchParams();
    params.set("tab", "search");
    for (const key of sentenceFilterKeys) {
      if (next[key] && next[key] !== initialFilters[key]) {
        params.set(key, next[key]);
      }
    }
    if (next.query) params.set("q", next.query);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }

  function applyCaseFilters(next: CaseFilters) {
    setCaseFilters(next);
    const params = new URLSearchParams();
    params.set("tab", "search");
    params.set("mode", "cases");
    for (const key of caseFilterKeys) {
      if (next[key] && next[key] !== initialCaseFilters[key]) {
        params.set(key, next[key]);
      }
    }
    if (next.query) params.set("q", next.query);
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }

  function switchSearchMode(mode: SearchMode) {
    setSearchMode(mode);
    setCaseDetail(null);
    setEventDetail(null);
    setCaseProfileDetail(null);
    const params = new URLSearchParams(window.location.search);
    if (mode === "case") {
      params.set("mode", "cases");
    } else {
      params.delete("mode");
    }
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }

  async function loadCaseDetail(caseNumber: string) {
    const response = await fetch(`/api/cases/${encodeURIComponent(caseNumber)}`);
    if (!response.ok) throw new Error("Unable to load case detail");
    setCaseDetail(await response.json());
    setCaseProfileDetail(null);
    setEventDetail(null);
  }

  async function loadCaseProfileDetail(row: CaseSearchRow) {
    const [charges, highlights] = await Promise.all([
      caseCharges ? Promise.resolve(caseCharges) : loadCaseCharges().then((rows) => {
        setCaseCharges(rows);
        return rows;
      }),
      caseEventHighlights ? Promise.resolve(caseEventHighlights) : loadCaseEventHighlights().then((rows) => {
        setCaseEventHighlights(rows);
        return rows;
      })
    ]);

    let apiQuality: Array<Record<string, unknown>> = [];
    let apiEvents: CaseEventHighlight[] = [];
    try {
      const [profileResponse, eventsResponse] = await Promise.all([
        fetch(`/api/case-profiles/${encodeURIComponent(row.case_number)}`),
        fetch(`/api/case-profiles/${encodeURIComponent(row.case_number)}/events?limit=250`)
      ]);
      if (profileResponse.ok) {
        const profilePayload = await profileResponse.json() as { quality?: Array<Record<string, unknown>> };
        apiQuality = profilePayload.quality ?? [];
      }
      if (eventsResponse.ok) {
        const eventPayload = await eventsResponse.json() as { results?: CaseEventHighlight[] };
        apiEvents = eventPayload.results ?? [];
      }
    } catch {
      apiQuality = [];
      apiEvents = [];
    }

    setCaseProfileDetail({
      profile: row,
      charges: charges.filter((charge) => charge.case_number === row.case_number),
      events: apiEvents.length ? apiEvents : highlights.filter((event) => event.case_number === row.case_number).slice(0, 250),
      quality: apiQuality,
      financial: data?.financialPreview.cases.find((financialRow) => financialRow.case_number === row.case_number)
    });
    setCaseDetail(null);
    setEventDetail(null);
  }

  async function loadEventDetail(id: number) {
    const response = await fetch(`/api/sentence-events/${id}`);
    if (!response.ok) throw new Error("Unable to load sentence event detail");
    setEventDetail(await response.json());
  }

  async function runEventSearch(offset = 0) {
    const params = new URLSearchParams();
    params.set("limit", "25");
    params.set("offset", String(offset));
    for (const [key, value] of Object.entries(eventSearchFilters)) {
      if (value) params.set(key, value);
    }
    setEventSearchLoading(true);
    try {
      const response = await fetch(`/api/case-events?${params.toString()}`);
      if (!response.ok) throw new Error("Unable to load procedural events");
      setEventSearchPayload(await response.json());
    } catch (err) {
      setError(String(err));
    } finally {
      setEventSearchLoading(false);
    }
  }

  async function loadQualityIssue(issue: string, offset = 0) {
    if (!issue) {
      setQualityAffectedPayload(null);
      setQualityIssue("");
      return;
    }
    const params = new URLSearchParams({ issue, limit: "25", offset: String(offset) });
    setQualityIssue(issue);
    setQualityLoading(true);
    try {
      const response = await fetch(`/api/data-quality?${params.toString()}`);
      if (!response.ok) throw new Error("Unable to load affected cases");
      setQualityAffectedPayload(await response.json());
    } catch (err) {
      setError(String(err));
    } finally {
      setQualityLoading(false);
    }
  }

  function saveComparisonSet() {
    const name = `Comparison ${savedComparisons.length + 1}`;
    const next = [...savedComparisons, { name, filters }];
    setSavedComparisons(next);
    localStorage.setItem("scjd-saved-comparisons", JSON.stringify(next));
  }

  function applyNaturalQuery() {
    setActiveTab("search");
    applyFilters(parseNaturalQuery(filters));
  }

  if (error) {
    return <main className="state-page">Unable to load sentencing data: {error}</main>;
  }

  if (!data) {
    return <main className="state-page">Loading sentencing data...</main>;
  }

  const sampleWarning = filteredRows.length < 30 ? "Small filtered sample. Compare carefully." : `${filteredRows.length.toLocaleString()} matching events`;

  return (
    <main>
      <header className="topbar">
        <div className="brand">
          <Scale aria-hidden="true" />
          <div>
            <h1>South Central Judicial Sentencing Data</h1>
            <p>Searchable sentencing events, conditions, probation, confinement, and source traceability.</p>
          </div>
        </div>
        <label className="global-search">
          <Search aria-hidden="true" />
          <input
            id="global-search"
            name="global-search"
            value={filters.query}
            onChange={(event) => updateFilter("query", event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") applyNaturalQuery();
            }}
            placeholder="Search case, source, title, agency"
          />
        </label>
      </header>

      <nav className="tabs" aria-label="Main sections">
        {[
          "dashboard",
          "search",
          "judges",
          "financial",
          "conditions",
          "trends",
          "methodology"
          // dictionary, downloads, and about are still under construction - will be made available later.
        ].map((tab) => (
          <button key={tab} className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>
            {tab[0].toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </nav>

      {activeTab === "dashboard" && (
        <section className="page-grid">
          <section className="panel">
            <div className="panel-header">
              <div>
                <h2>Dashboard View</h2>
                <p>{dashboardMode === "sentencing" ? "Sentence-event metrics from the original sentencing extract." : "Felony case context from cases, charges, docket events, and audit data."}</p>
              </div>
              <div className="segmented-control" aria-label="Dashboard data mode">
                <button className={dashboardMode === "sentencing" ? "active" : ""} onClick={() => setDashboardMode("sentencing")}>Sentencing</button>
                <button className={dashboardMode === "cases" ? "active" : ""} onClick={() => setDashboardMode("cases")}>Cases</button>
              </div>
            </div>
          </section>
          {dashboardMode === "sentencing" ? (
            <>
              <CompactMetricStrip>
                <MetricCard label="Sentence events" value={numberOrDash(data.summary.totalSentenceEvents)} note={`${shortDate(data.summary.dateMin)} to ${shortDate(data.summary.dateMax)}`} sparkline={<Sparkline values={data.charts.monthlyEvents.map((row) => row.events)} color={colors.sentence} label="Sentence events monthly trend" />} />
                <MetricCard label="Cases linked" value={numberOrDash(data.summary.totalCases)} note="Unique case numbers" />
                <MetricCard label="Median confinement" value={numberOrDash(data.summary.medianConfinementImposed, " mo")} note="Sentence events, nulls excluded" />
                <MetricCard label="Median probation" value={numberOrDash(data.summary.medianProbation, " mo")} note="Sentence events, nulls excluded" />
                <MetricCard label="Revocation share" value={percent(data.summary.revocationShare)} note="All sentence events" sparkline={<Sparkline values={data.charts.monthlyRevocations.map((row) => row.revocationShare)} color={colors.warning} label="Revocation share monthly trend" />} />
              </CompactMetricStrip>
              <section className="analysis-grid">
                <article className="panel primary-analysis plot-tall">
                  <h2>Case Lifecycle Overview</h2>
                  <p className="chart-note">Case-level path through major milestones. Bars show approximate case counts; labels include median days where available.</p>
                  <Plot title="case-lifecycle-overview" description="Case lifecycle overview with filed cases, first plea, first judgment, first revocation, and final disposition counts plus median days." data={[{ type: "bar", orientation: "h", y: lifecycleRows.map((row) => row.stage).reverse(), x: lifecycleRows.map((row) => row.cases).reverse(), text: lifecycleRows.map((row) => row.days === null ? `${row.cases.toLocaleString()} cases` : `${row.cases.toLocaleString()} cases | median ${row.days} days`).reverse(), textposition: "auto", marker: { color: colors.case }, hovertemplate: "%{y}<br>%{text}<extra></extra>" }]} layout={{ margin: { l: 150, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartEvidence unit="case profiles" denominator={`${data.caseContextSummary.uniqueCaseNumbers.toLocaleString()} unique cases`} source="felony case export" caveat="Plea, revocation, and disposition stages use available parsed milestone fields." />
                </article>
                <article className="panel">
                  <h2>Sentence Context</h2>
                  <p className="chart-note">Sentence-event medians and condition shares are separate from case-level lifecycle counts.</p>
                  <DenseComparisonTable
                    rows={[
                      { metric: "Avg confinement", value: data.summary.avgConfinementImposed, context: "months" },
                      { metric: "Avg suspended", value: data.summary.avgConfinementSuspended, context: "months" },
                      { metric: "Avg probation", value: data.summary.avgProbation, context: "months" },
                      { metric: "Avg credit served", value: data.summary.avgCreditDays, context: "days" },
                      { metric: "Supervised probation", value: data.summary.pctSupervisedProbation, context: "% events" },
                      { metric: "Treatment required", value: data.summary.pctTreatment, context: "% events" },
                      { metric: "Evaluation required", value: data.summary.pctEvaluation, context: "% events" }
                    ]}
                    labelKey="metric"
                    valueKey="value"
                    percentKey="context"
                    color={colors.sentence}
                  />
                </article>
              </section>
              <section className="chart-grid">
                <article className="panel primary-analysis">
                  <h2>Offense Category Sentence Comparison</h2>
                  <p className="chart-note">Direct dashboard comparison of offense categories by sentence-event count, average confinement, average probation, and revocation share.</p>
                  <ChartTable rows={dashboardOffenseRows} columns={["category", "events", "confinement", "probation", "revocation"]} barColumn="events" color={colors.case} />
                  <ChartEvidence unit="sentence events joined to case-level primary offense category" denominator="category-specific sentence events" source="sentencing extract plus felony case export" caveat="Primary charge category may not describe every charge in a case." />
                </article>
                <article className="panel">
                  <h2>Sentence Type Share</h2>
                  <p className="chart-note">Percent of all sentence events by normalized type.</p>
                  <Plot title="sentence-types" description="Horizontal bar chart showing original and revocation sentence-event shares." data={[{ type: "bar", orientation: "h", y: sentenceTypeShare.map((d) => d.label).reverse(), x: sentenceTypeShare.map((d) => d.percent).reverse(), customdata: sentenceTypeShare.map((d) => d.label).reverse(), text: sentenceTypeShare.map((d) => `${displayPercent(d.percent)} (${d.value.toLocaleString()} events)`).reverse(), textposition: "auto", marker: { color: colors.sentence }, hovertemplate: "%{y}<br>%{text}<extra></extra>" }]} layout={{ margin: { l: 130, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "% of sentence events", standoff: 14 }, ticksuffix: "%", range: [0, 100], automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      applyFilters({ ...filters, sentenceType: value });
                    }
                  }} />
                  <ChartTable rows={sentenceTypeShare.map((row) => ({ type: row.label, events: row.value, "% of events": displayPercent(row.percent) }))} barColumn="events" color={colors.sentence} />
                  <ChartEvidence unit="sentence events" denominator={`${data.summary.totalSentenceEvents.toLocaleString()} sentence events`} source="sentencing extract" />
                </article>
                <article className="panel">
                  <h2>Confinement Imposed</h2>
                  <p className="chart-note">Exact month values are grouped into ranges so common sentence lengths and outliers are easier to compare.</p>
                  <Plot title="confinement-distribution" description="Binned sentence-event confinement distribution." data={[{ type: "bar", x: confinementBins.map((d) => d.label), y: confinementBins.map((d) => d.events), text: confinementBins.map((d) => displayPercent(d.percent)), textposition: "auto", marker: { color: colors.case }, hovertemplate: "%{x}<br>%{y:,} events<br>%{text} of events with confinement data<extra></extra>" }]} layout={{ margin: { l: 82, r: 24, t: 20, b: 108 }, xaxis: { title: { text: "Confinement imposed", standoff: 16 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Sentence events", standoff: 14 }, automargin: true } }} />
                  <ChartTable rows={confinementBins.map((row) => ({ range: row.label, events: row.events, "% of events": displayPercent(row.percent) }))} barColumn="events" color={colors.case} />
                </article>
                <article className="panel">
                  <h2>Probation Ordered</h2>
                  <p className="chart-note">Exact month values are grouped into ranges so common probation lengths and outliers are easier to compare.</p>
                  <Plot title="probation-distribution" description="Binned sentence-event probation distribution." data={[{ type: "bar", x: probationBins.map((d) => d.label), y: probationBins.map((d) => d.events), text: probationBins.map((d) => displayPercent(d.percent)), textposition: "auto", marker: { color: colors.condition }, hovertemplate: "%{x}<br>%{y:,} events<br>%{text} of events with probation data<extra></extra>" }]} layout={{ margin: { l: 82, r: 24, t: 20, b: 108 }, xaxis: { title: { text: "Probation ordered", standoff: 16 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Sentence events", standoff: 14 }, automargin: true } }} />
                  <ChartTable rows={probationBins.map((row) => ({ range: row.label, events: row.events, "% of events": displayPercent(row.percent) }))} barColumn="events" color={colors.condition} />
                </article>
                <article className="panel">
                  <h2>Common Event Titles</h2>
                  <p className="chart-note">Top document or event titles by sentence-event count.</p>
                  <Plot title="event-titles" description="Top sentence-event title counts." data={[{ type: "bar", orientation: "h", y: commonEventTitles.map((d) => d.label).reverse(), x: commonEventTitles.map((d) => d.value).reverse(), text: commonEventTitles.map((d) => d.value.toLocaleString()).reverse(), textposition: "auto", marker: { color: colors.warning }, hovertemplate: "%{y}<br>%{x:,} sentence events<extra></extra>" }]} layout={{ margin: { l: 330, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Sentence events", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.charts.eventTitles} barColumn="value" color={colors.warning} />
                </article>
              </section>
            </>
          ) : (
            <>
              <CompactMetricStrip>
                <MetricCard label="Felony cases" value={numberOrDash(data.caseContextSummary.uniqueCaseNumbers)} note={`${shortDate(data.caseContextSummary.filedDateMin)} to ${shortDate(data.caseContextSummary.filedDateMax)}`} sparkline={<Sparkline values={data.caseContextCharts.monthlyFiled.map((row) => row.cases)} color={colors.case} label="Monthly case filings" />} />
                <MetricCard label="Charges" value={numberOrDash(data.caseContextSummary.totalCharges)} note="Charge-level rows" />
                <MetricCard label="Docket events" value={numberOrDash(data.caseContextSummary.totalDocketEvents)} note={`${shortDate(data.caseContextSummary.eventDateMin)} to ${shortDate(data.caseContextSummary.eventDateMax)}`} sparkline={<Sparkline values={data.caseContextCharts.monthlyEvents.map((row) => row.events)} color={colors.procedure} label="Monthly docket events" />} />
                <MetricCard label="Revocation activity" value={percent(data.caseContextSummary.revocationCaseShare)} note="Cases with flag" />
                <MetricCard label="Median days to judgment" value={numberOrDash(data.caseContextSummary.medianDaysToJudgment, " days")} note="Nulls excluded" />
              </CompactMetricStrip>
              <section className="chart-grid">
                <article className="panel">
                  <h2>County Distribution</h2>
                  <p className="chart-note">Case-level count by county.</p>
                  <Plot title="case-counties" description="Case-level county count comparison." data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.counties.slice(0, 10).map((d) => d.label).reverse(), customdata: data.caseContextCharts.counties.slice(0, 10).map((d) => d.label).reverse(), x: data.caseContextCharts.counties.slice(0, 10).map((d) => d.value).reverse(), marker: { color: colors.case }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 170, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      switchSearchMode("case");
                      applyCaseFilters({ ...caseFilters, county: value });
                    }
                  }} />
                  <ChartTable rows={data.caseContextCharts.counties} barColumn="value" color={colors.case} />
                  <ChartEvidence unit="case profiles" denominator={`${data.caseContextSummary.uniqueCaseNumbers.toLocaleString()} unique cases`} source="felony case export" />
                </article>
                <article className="panel">
                  <h2>Outcome Buckets</h2>
                  <p className="chart-note">Plain-language case outcome group from the felony export.</p>
                  <Plot title="case-outcomes" description="Case-level outcome bucket counts." data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.outcomeBuckets.map((d) => labelize(d.label)).reverse(), customdata: data.caseContextCharts.outcomeBuckets.map((d) => d.label).reverse(), x: data.caseContextCharts.outcomeBuckets.map((d) => d.value).reverse(), marker: { color: colors.warning }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 260, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      switchSearchMode("case");
                      applyCaseFilters({ ...caseFilters, outcome: value });
                    }
                  }} />
                  <ChartTable rows={data.caseContextCharts.outcomeBuckets.map((row) => ({ outcome: labelize(row.label), cases: row.value }))} barColumn="cases" color={colors.warning} />
                </article>
                <article className="panel">
                  <h2>Primary Offense Category</h2>
                  <p className="chart-note">Case-level category derived from the primary charge.</p>
                  <Plot title="offense-categories" data={[{ type: "bar", x: data.caseContextCharts.primaryOffenseCategories.map((d) => labelize(d.label)), customdata: data.caseContextCharts.primaryOffenseCategories.map((d) => d.label), y: data.caseContextCharts.primaryOffenseCategories.map((d) => d.value), marker: { color: "#047857" }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ margin: { l: 82, r: 24, t: 20, b: 110 }, xaxis: { title: { text: "Primary offense category", standoff: 16 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      switchSearchMode("case");
                      applyCaseFilters({ ...caseFilters, offenseCategory: value });
                    }
                  }} />
                  <ChartTable rows={data.caseContextCharts.primaryOffenseCategories.map((row) => ({ category: labelize(row.label), cases: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Primary Charge Level</h2>
                  <p className="chart-note">Case-level primary charge severity.</p>
                  <Plot title="primary-charge-levels" data={[{ type: "bar", x: data.caseContextCharts.primaryChargeLevels.map((d) => d.label), customdata: data.caseContextCharts.primaryChargeLevels.map((d) => d.label), y: data.caseContextCharts.primaryChargeLevels.map((d) => d.value), marker: { color: "#9333ea" }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ xaxis: { title: { text: "Primary charge level", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      switchSearchMode("case");
                      applyCaseFilters({ ...caseFilters, chargeLevel: value });
                    }
                  }} />
                  <ChartTable rows={data.caseContextCharts.primaryChargeLevels.map((row) => ({ level: row.label, cases: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Case Filings Over Time</h2>
                  <p className="chart-note">Monthly case filings from the felony export.</p>
                  <Plot title="case-filings" data={[{ type: "scatter", mode: "lines+markers", x: data.caseContextCharts.monthlyFiled.map((d) => d.month), y: data.caseContextCharts.monthlyFiled.map((d) => d.cases), line: { color: "#2563eb" }, hovertemplate: "%{x}<br>%{y:,} cases filed<extra></extra>" }]} layout={{ xaxis: { title: { text: "Filed month", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.monthlyFiled} />
                </article>
                <article className="panel">
                  <h2>Charges And Offenses</h2>
                  <p className="chart-note">Charge-level count by charge severity. A case can have multiple charges.</p>
                  <Plot title="charge-levels" data={[{ type: "bar", x: data.caseContextCharts.chargeLevels.map((d) => d.label), customdata: data.caseContextCharts.chargeLevels.map((d) => d.label), y: data.caseContextCharts.chargeLevels.map((d) => d.value), marker: { color: "#7c3aed" }, hovertemplate: "%{x}<br>%{y:,} charges<extra></extra>" }]} layout={{ xaxis: { title: { text: "Charge level", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Charges", standoff: 14 }, automargin: true } }} onPointClick={(value) => {
                    if (typeof value === "string") {
                      setActiveTab("search");
                      switchSearchMode("case");
                      applyCaseFilters({ ...caseFilters, chargeLevel: value });
                    }
                  }} />
                  <ChartTable rows={data.caseContextCharts.chargeLevels.map((row) => ({ level: row.label, charges: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Top Charge Descriptions</h2>
                  <p className="chart-note">Most common charge descriptions after export normalization. Use case search for exact wording review.</p>
                  <Plot title="top-charge-descriptions" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.topChargeDescriptions.slice(0, 12).map((d) => d.label).reverse(), x: data.caseContextCharts.topChargeDescriptions.slice(0, 12).map((d) => d.value).reverse(), marker: { color: "#2563eb" }, hovertemplate: "%{y}<br>%{x:,} charges<extra></extra>" }]} layout={{ margin: { l: 300, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Charges", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.topChargeDescriptions.map((row) => ({ description: row.label, charges: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Top Statutes</h2>
                  <p className="chart-note">Most frequent charge statutes in the charge-level export.</p>
                  <Plot title="top-statutes" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.topStatutes.slice(0, 12).map((d) => d.label).reverse(), x: data.caseContextCharts.topStatutes.slice(0, 12).map((d) => d.value).reverse(), marker: { color: "#047857" }, hovertemplate: "%{y}<br>%{x:,} charges<extra></extra>" }]} layout={{ margin: { l: 180, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Charges", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.topStatutes.map((row) => ({ statute: row.label, charges: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Charge-Level Date Trends</h2>
                  <p className="chart-note">Monthly offense-date counts by charge level. Counts are charges, not cases.</p>
                  <Plot title="charge-level-date-trends" data={chartLevels.map((level, index) => ({
                    type: "scatter",
                    mode: "lines",
                    name: level,
                    x: data.caseContextCharts.monthlyOffenses.map((d) => d.month),
                    y: data.caseContextCharts.monthlyOffenses.map((monthRow) => data.caseContextCharts.monthlyChargeLevels.find((row) => row.month === monthRow.month && row.level === level)?.charges ?? 0),
                    line: { color: ["#2563eb", "#047857", "#b45309", "#be123c", "#7c3aed", "#64748b", "#0891b2", "#4f46e5"][index % 8] },
                    hovertemplate: "%{x}<br>%{y:,} charges<extra></extra>"
                  }))} layout={{ yaxis: { title: { text: "Charges", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Offense month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
                  <ChartTable rows={data.caseContextCharts.monthlyChargeLevels} />
                </article>
                <article className="panel">
                  <h2>Average Charges Per Case</h2>
                  <p className="chart-note">County/offense combinations with at least 10 cases. X-axis is average charge rows per case.</p>
                  <Plot title="average-charges-by-county-offense" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.averageChargesByCountyOffense.slice(0, 20).map((row) => `${row.county} / ${labelize(row.category)}`).reverse(), x: data.caseContextCharts.averageChargesByCountyOffense.slice(0, 20).map((row) => row.avgCharges ?? 0).reverse(), marker: { color: "#b45309" }, hovertemplate: "%{y}<br>%{x} charges per case<extra></extra>" }]} layout={{ margin: { l: 300, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Average charges per case", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.averageChargesByCountyOffense.map((row) => ({ county: row.county, category: labelize(row.category), cases: row.cases, "avg charges": row.avgCharges }))} />
                </article>
                <article className="panel">
                  <h2>Procedural Event Buckets</h2>
                  <p className="chart-note">Docket/procedural events grouped by event bucket.</p>
                  <Plot title="case-event-buckets" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.eventBuckets.map((d) => labelize(d.label)).reverse(), x: data.caseContextCharts.eventBuckets.map((d) => d.value).reverse(), marker: { color: "#b45309" }, hovertemplate: "%{y}<br>%{x:,} events<extra></extra>" }]} layout={{ margin: { l: 190, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Docket events", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.eventBuckets.map((row) => ({ bucket: labelize(row.label), events: row.value }))} />
                </article>
                <article className="panel">
                  <h2>Data Quality Checks</h2>
                  <p className="chart-note">Parser/audit checks imported from the case export. Counts describe affected rows or cases.</p>
                  <Plot title="case-quality" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.qualityChecks.map((d) => labelize(d.label)).reverse(), x: data.caseContextCharts.qualityChecks.map((d) => d.value).reverse(), marker: { color: "#64748b" }, hovertemplate: "%{y}<br>%{x:,} affected<extra></extra>" }]} layout={{ margin: { l: 280, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Affected rows/cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
                  <ChartTable rows={data.caseContextCharts.qualityChecks.map((row) => ({ check: labelize(row.label), affected: row.value, severity: row.severity }))} />
                </article>
              </section>
            </>
          )}
        </section>
      )}

      {activeTab === "search" && (
        <section className="search-layout">
          <aside className="filters" aria-label="Advanced filters">
            <h2><Filter aria-hidden="true" /> Filters</h2>
            <div className="segmented-control" aria-label="Search mode">
              <button className={searchMode === "sentence" ? "active" : ""} onClick={() => switchSearchMode("sentence")}>Sentence events</button>
              <button className={searchMode === "case" ? "active" : ""} onClick={() => switchSearchMode("case")}>Cases</button>
            </div>
            {searchMode === "sentence" ? (
              <>
                <label htmlFor="date-from">Date from<input id="date-from" name="date-from" type="date" value={filters.dateFrom} onChange={(event) => updateFilter("dateFrom", event.target.value)} /></label>
                <button className="secondary" onClick={applyNaturalQuery}>Apply natural search</button>
                <label htmlFor="date-to">Date to<input id="date-to" name="date-to" type="date" value={filters.dateTo} onChange={(event) => updateFilter("dateTo", event.target.value)} /></label>
                <div className="preset-row" aria-label="Date range presets">
                  <button className="secondary" onClick={() => applyFilters({ ...filters, dateFrom: "2025-01-01", dateTo: "" })}>Since 2025</button>
                  <button className="secondary" onClick={() => applyFilters({ ...filters, dateFrom: "2020-01-01", dateTo: "" })}>Since 2020</button>
                  <button className="secondary" onClick={() => applyFilters({ ...filters, dateFrom: "", dateTo: "" })}>All dates</button>
                </div>
                <label htmlFor="sentence-type">Sentence type<select id="sentence-type" name="sentence-type" value={filters.sentenceType} onChange={(event) => updateFilter("sentenceType", event.target.value)}><option value="">Any</option>{sentenceTypes.map((type) => <option key={type} value={type ?? ""}>{type}</option>)}</select></label>
                <label htmlFor="event-title">Event title<select id="event-title" name="event-title" value={filters.eventTitle} onChange={(event) => updateFilter("eventTitle", event.target.value)}><option value="">Any</option>{eventTitles.map((title) => <option key={title} value={title ?? ""}>{title}</option>)}</select></label>
                <label htmlFor="judge">Judge<select id="judge" name="judge" value={filters.judge} onChange={(event) => updateFilter("judge", event.target.value)}><option value="">Any</option>{judgeNames.length ? judgeNames.map((judge) => <option key={judge} value={judge}>{judge}</option>) : <option value="" disabled>Judge data unavailable</option>}</select></label>
                <label htmlFor="sentence-case-county">Case county<select id="sentence-case-county" name="sentence-case-county" value={filters.caseCounty} onChange={(event) => updateFilter("caseCounty", event.target.value)}><option value="">Any</option>{sentenceCaseCounties.map((county) => <option key={county} value={county}>{county}</option>)}</select></label>
                <label htmlFor="sentence-case-judge">Initial judicial officer<select id="sentence-case-judge" name="sentence-case-judge" value={filters.caseInitialJudge} onChange={(event) => updateFilter("caseInitialJudge", event.target.value)}><option value="">Any</option>{sentenceCaseJudges.map((judge) => <option key={judge} value={judge}>{judge}</option>)}</select></label>
                <label htmlFor="sentence-case-category">Offense category<select id="sentence-case-category" name="sentence-case-category" value={filters.caseOffenseCategory} onChange={(event) => updateFilter("caseOffenseCategory", event.target.value)}><option value="">Any</option>{sentenceCaseCategories.map((category) => <option key={category} value={category}>{labelize(category)}</option>)}</select></label>
                <label htmlFor="sentence-case-level">Charge level<select id="sentence-case-level" name="sentence-case-level" value={filters.caseChargeLevel} onChange={(event) => updateFilter("caseChargeLevel", event.target.value)}><option value="">Any</option>{sentenceCaseLevels.map((level) => <option key={level} value={level}>{level}</option>)}</select></label>
                <label htmlFor="sentence-case-revocation">Later revocation<select id="sentence-case-revocation" name="sentence-case-revocation" value={filters.caseRevocation} onChange={(event) => updateFilter("caseRevocation", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="sentence-case-warrant">Warrant activity<select id="sentence-case-warrant" name="sentence-case-warrant" value={filters.caseWarrant} onChange={(event) => updateFilter("caseWarrant", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="condition-flag">Condition flag<select id="condition-flag" name="condition-flag" value={filters.conditionFlag} onChange={(event) => updateFilter("conditionFlag", event.target.value)}><option value="">Any</option>{Object.entries(flagLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
                <label htmlFor="condition-title">Condition title<select id="condition-title" name="condition-title" value={filters.conditionTitle} onChange={(event) => updateFilter("conditionTitle", event.target.value)}><option value="">Any</option>{conditionTitles.map((title) => <option key={title} value={title}>{title}</option>)}</select></label>
                <label htmlFor="condition-status">Condition status<select id="condition-status" name="condition-status" value={filters.conditionStatus} onChange={(event) => updateFilter("conditionStatus", event.target.value)}><option value="">Any</option>{conditionStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
                <label htmlFor="min-confinement">Minimum confinement<input id="min-confinement" name="min-confinement" type="number" min="0" value={filters.minConfinement} onChange={(event) => updateFilter("minConfinement", event.target.value)} /></label>
                <label htmlFor="min-suspended">Minimum suspended<input id="min-suspended" name="min-suspended" type="number" min="0" value={filters.minSuspended} onChange={(event) => updateFilter("minSuspended", event.target.value)} /></label>
                <label htmlFor="min-credit">Minimum credit served<input id="min-credit" name="min-credit" type="number" min="0" value={filters.minCredit} onChange={(event) => updateFilter("minCredit", event.target.value)} /></label>
                <label htmlFor="min-first-serve">Minimum first serve<input id="min-first-serve" name="min-first-serve" type="number" min="0" value={filters.minFirstServe} onChange={(event) => updateFilter("minFirstServe", event.target.value)} /></label>
                <label htmlFor="min-probation">Minimum probation<input id="min-probation" name="min-probation" type="number" min="0" value={filters.minProbation} onChange={(event) => updateFilter("minProbation", event.target.value)} /></label>
                <label htmlFor="sort">Sort<select id="sort" name="sort" value={filters.sort} onChange={(event) => updateFilter("sort", event.target.value)}><option value="date_desc">Newest first</option><option value="case">Case number</option><option value="judge">Judge</option><option value="confinement">Confinement</option><option value="probation">Probation</option><option value="conditions">Condition count</option></select></label>
                <button className="secondary" onClick={() => applyFilters(initialFilters)}>Clear filters</button>
                <button className="secondary" onClick={saveComparisonSet}>Save comparison</button>
                {savedComparisons.length ? (
                  <div className="saved-sets">
                    <h3>Saved sets</h3>
                    {savedComparisons.map((set) => (
                      <button key={set.name} className="secondary" onClick={() => applyFilters(set.filters)}>{set.name}</button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <label htmlFor="case-query">Case search<input id="case-query" name="case-query" value={caseFilters.query} onChange={(event) => updateCaseFilter("query", event.target.value)} placeholder="Case, charge, county, judge" /></label>
                <label htmlFor="filed-from">Filed from<input id="filed-from" name="filed-from" type="date" value={caseFilters.filedFrom} onChange={(event) => updateCaseFilter("filedFrom", event.target.value)} /></label>
                <label htmlFor="filed-to">Filed to<input id="filed-to" name="filed-to" type="date" value={caseFilters.filedTo} onChange={(event) => updateCaseFilter("filedTo", event.target.value)} /></label>
                <label htmlFor="offense-from">Offense from<input id="offense-from" name="offense-from" type="date" value={caseFilters.offenseFrom} onChange={(event) => updateCaseFilter("offenseFrom", event.target.value)} /></label>
                <label htmlFor="offense-to">Offense to<input id="offense-to" name="offense-to" type="date" value={caseFilters.offenseTo} onChange={(event) => updateCaseFilter("offenseTo", event.target.value)} /></label>
                <label htmlFor="case-county">County<select id="case-county" name="case-county" value={caseFilters.county} onChange={(event) => updateCaseFilter("county", event.target.value)}><option value="">Any</option>{caseCounties.map((county) => <option key={county} value={county}>{county}</option>)}</select></label>
                <label htmlFor="case-judge">Initial judicial officer<select id="case-judge" name="case-judge" value={caseFilters.judge} onChange={(event) => updateCaseFilter("judge", event.target.value)}><option value="">Any</option>{caseJudgeNames.map((judge) => <option key={judge} value={judge}>{judge}</option>)}</select></label>
                <label htmlFor="case-category">Offense category<select id="case-category" name="case-category" value={caseFilters.offenseCategory} onChange={(event) => updateCaseFilter("offenseCategory", event.target.value)}><option value="">Any</option>{caseOffenseCategories.map((category) => <option key={category} value={category}>{labelize(category)}</option>)}</select></label>
                <label htmlFor="case-level">Primary charge level<select id="case-level" name="case-level" value={caseFilters.chargeLevel} onChange={(event) => updateCaseFilter("chargeLevel", event.target.value)}><option value="">Any</option>{caseChargeLevels.map((level) => <option key={level} value={level}>{level}</option>)}</select></label>
                <label htmlFor="case-outcome">Outcome<select id="case-outcome" name="case-outcome" value={caseFilters.outcome} onChange={(event) => updateCaseFilter("outcome", event.target.value)}><option value="">Any</option>{caseOutcomes.map((outcome) => <option key={outcome} value={outcome}>{labelize(outcome)}</option>)}</select></label>
                <label htmlFor="event-flag">Procedural event<select id="event-flag" name="event-flag" value={caseFilters.eventFlag} onChange={(event) => updateCaseFilter("eventFlag", event.target.value)}><option value="">Any</option>{Object.entries(eventFlagLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
                <label htmlFor="case-revocation">Revocation activity<select id="case-revocation" name="case-revocation" value={caseFilters.revocation} onChange={(event) => updateCaseFilter("revocation", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-warrant">Warrant activity<select id="case-warrant" name="case-warrant" value={caseFilters.warrant} onChange={(event) => updateCaseFilter("warrant", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-pled">Pled guilty<select id="case-pled" name="case-pled" value={caseFilters.pledGuilty} onChange={(event) => updateCaseFilter("pledGuilty", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-dismissed">Dismissed ever<select id="case-dismissed" name="case-dismissed" value={caseFilters.dismissed} onChange={(event) => updateCaseFilter("dismissed", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-trial">Trial set<select id="case-trial" name="case-trial" value={caseFilters.trialSet} onChange={(event) => updateCaseFilter("trialSet", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-appointed">Appointed counsel<select id="case-appointed" name="case-appointed" value={caseFilters.appointedCounsel} onChange={(event) => updateCaseFilter("appointedCounsel", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-private">Private counsel<select id="case-private" name="case-private" value={caseFilters.privateCounsel} onChange={(event) => updateCaseFilter("privateCounsel", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
                <label htmlFor="case-defense-type">Defense type<select id="case-defense-type" name="case-defense-type" value={caseFilters.defenseType} onChange={(event) => updateCaseFilter("defenseType", event.target.value)}><option value="">Any</option>{caseDefenseTypes.map((type) => <option key={type} value={type}>{labelize(type)}</option>)}</select></label>
                <label htmlFor="case-sort">Sort<select id="case-sort" name="case-sort" value={caseFilters.sort} onChange={(event) => updateCaseFilter("sort", event.target.value)}><option value="filed_desc">Newest filed</option><option value="case">Case number</option><option value="judge">Judge</option><option value="charges">Charge count</option><option value="events">Event count</option><option value="judgment_days">Days to judgment</option></select></label>
                <button className="secondary" onClick={() => applyCaseFilters(initialCaseFilters)}>Clear case filters</button>
              </>
            )}
          </aside>
          <section className="panel results-panel">
            {searchMode === "sentence" ? (
              <>
                <div className="panel-header">
                  <div>
                    <h2>Sentence Events</h2>
                    <p>{sampleWarning}. Missing values are unavailable, not zero.</p>
                  </div>
                  <button onClick={() => exportCsv(filteredRows)}><Download aria-hidden="true" /> Export CSV</button>
                </div>
                <div className="chips">
                  {activeEntries(filters, initialFilters).map(([key, value]) => (
                    <span key={key}>{sentenceFilterLabels[key as keyof Filters] ?? key}: {sentenceFilterValueLabel(key, value)}</span>
                  ))}
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Case</th><th>Date</th><th>Title</th><th>Type</th><th>Judge</th><th>Confinement</th><th>Probation</th><th>Conditions</th><th>Source</th></tr></thead>
                    <tbody>
                      {filteredRows.slice(0, 250).map((row) => (
                        <tr key={row.sentence_event_id}>
                          <td><button className="link-button" onClick={() => loadCaseDetail(row.case_number)}>{row.case_number}</button></td>
                          <td>{shortDate(row.sentence_event_date)}</td>
                          <td><button className="link-button" onClick={() => loadEventDetail(row.sentence_event_id)}>{row.sentence_event_title ?? "Unavailable"}</button></td>
                          <td>{row.sentence_type ?? "Unavailable"}</td>
                          <td>{row.judge_names ?? row.case_judicial_officer_initial ?? "Unavailable"}</td>
                          <td>{numberOrDash(row.confinement_months_imposed, " mo")}</td>
                          <td>{numberOrDash(row.probation_months, " mo")}</td>
                          <td>{row.condition_count}</td>
                          <td>{row.source_file ?? "Unavailable"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {caseDetail ? (
                  <DetailPanel title="Case Timeline" onClose={() => setCaseDetail(null)}>
                    <CaseDetail payload={caseDetail} />
                  </DetailPanel>
                ) : null}
                {eventDetail ? (
                  <DetailPanel title="Sentence Event Detail" onClose={() => setEventDetail(null)}>
                    <EventDetail payload={eventDetail} query={filters.query} />
                  </DetailPanel>
                ) : null}
              </>
            ) : (
              <>
                <div className="panel-header">
                  <div>
                    <h2>Case Profiles</h2>
                    <p>{caseDataLoading ? "Loading case profiles..." : `${filteredCaseRows.length.toLocaleString()} matching cases`}. Case rows omit defendant names and sensitive identifiers.</p>
                  </div>
                  <button onClick={() => exportCaseCsv(filteredCaseRows)}><Download aria-hidden="true" /> Export CSV</button>
                </div>
                <div className="chips">
                  {activeEntries(caseFilters, initialCaseFilters).map(([key, value]) => (
                    <span key={key}>{caseFilterLabels[key as keyof CaseFilters] ?? key}: {caseFilterValueLabel(key, value)}</span>
                  ))}
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Case</th><th>Filed</th><th>County</th><th>Judge</th><th>Outcome</th><th>Primary charge</th><th>Level</th><th>Charges</th><th>Events</th><th>Revocation</th><th>Warrant</th></tr></thead>
                    <tbody>
                      {filteredCaseRows.slice(0, 250).map((row) => (
                        <tr key={row.case_number}>
                          <td><button className="link-button" onClick={() => loadCaseProfileDetail(row)}>{row.case_number}</button></td>
                          <td>{shortDate(row.filed_date)}</td>
                          <td>{row.county ?? "Unavailable"}</td>
                          <td>{row.judicial_officer_initial ?? "Unavailable"}</td>
                          <td>{labelize(row.outcome_bucket)}</td>
                          <td>{row.primary_charge_description ?? "Unavailable"}</td>
                          <td>{row.primary_charge_level ?? "Unavailable"}</td>
                          <td>{numberOrDash(row.charge_count)}</td>
                          <td>{numberOrDash(row.event_count)}</td>
                          <td>{yesNo(row.revocation_ever)}</td>
                          <td>{yesNo(row.warrant_any_ever)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {caseProfileDetail ? (
                  <DetailPanel title="Case Profile Detail" onClose={() => setCaseProfileDetail(null)}>
                    <CaseProfileDetail payload={caseProfileDetail} />
                  </DetailPanel>
                ) : null}
              </>
            )}
          </section>
        </section>
      )}

      {activeTab === "judges" && (
        <section className="page-grid">
          <section className="panel prose printable-report">
            <h2>Judge Analytics</h2>
            <p>Judge context now comes from the felony case export. Case-level `judicial_officer_initial` is shown separately from sparse event-level judge fields and should not be read as sentencing-judge attribution unless the underlying sentence event confirms it.</p>
            <div className="notice">Event-level judge is missing on {numberOrDash(data.caseDataQuality.checks.find((check) => check.check_name === "missing_event_judicial_officer")?.affected_count)} docket events, so judge comparisons are case-mix context rather than complete event attribution.</div>
            <label className="standalone-label">Judge profile
              <select id="judge-profile" name="judge-profile" value={selectedJudge} onChange={(event) => setSelectedJudgeName(event.target.value)}>
                {data.caseJudges.map((judge) => <option key={judge.judge_id} value={judge.judge_name}>{judge.judge_name}</option>)}
              </select>
            </label>
          </section>
          <section className="metrics">
            <MetricCard label="Case-level judges" value={numberOrDash(data.caseContextSummary.totalInitialJudges)} note="Initial judicial officer field" />
            <MetricCard label="Event-level judges" value={numberOrDash(data.caseContextSummary.totalEventJudges)} note="Sparse procedural events" />
            <MetricCard label="Judge-linked cases" value={numberOrDash(data.caseJudges.reduce((sum, judge) => sum + judge.cases, 0))} note="Case-level assignment rows" />
            <MetricCard label="Small-sample rule" value="< 30" note="Judge comparisons warn on small samples" />
          </section>
          <section className="chart-grid">
            <article className="panel chart-grid-wide">
              <h2>Judge Comparison Small Multiples</h2>
              <p className="chart-note">Each panel uses the same structure: sample size, revocation share, warrant share, and average days to judgment. District markers show the baseline where available.</p>
              <div className="small-multiple-grid">
                {data.caseJudges.slice(0, 12).map((judge) => (
                  <div className="small-multiple" key={judge.judge_id}>
                    <h3>{judge.judge_name}</h3>
                    <span>{judge.cases.toLocaleString()} cases{judge.cases < 30 ? " | small sample" : ""}</span>
                    <RangeRow label="Revocation" value={judge.revocation_case_share} baseline={districtRevocationShare} color={judge.cases < 30 ? colors.reference : colors.warning} />
                    <RangeRow label="Warrant" value={judge.warrant_case_share} baseline={districtWarrantShare} color={judge.cases < 30 ? colors.reference : colors.procedure} />
                    <span>Avg days to judgment: {numberOrDash(judge.avg_days_to_judgment, " days")}</span>
                  </div>
                ))}
              </div>
              <ChartEvidence unit="case-level initial judicial officer assignments" denominator="cases assigned to each initial judicial officer" source="felony case export" caveat="These are case-mix context panels, not performance rankings" />
            </article>
            <article className="panel">
              <h2>Judge Case List</h2>
              {data.caseJudges.length ? (
                <div className="table-wrap compact">
                  <table>
                    <thead><tr><th>Judge</th><th>Cases</th><th>Counties</th><th>Filed range</th><th>Revocation activity</th><th>Warrant activity</th><th>Avg days to judgment</th></tr></thead>
                    <tbody>
                      {data.caseJudges.map((judge) => (
                        <tr key={judge.judge_id}>
                          <td>{judge.judge_name}</td>
                          <td>{judge.cases}</td>
                          <td>{judge.counties}</td>
                          <td>{shortDate(judge.first_filed_date)} to {shortDate(judge.last_filed_date)}</td>
                          <td>{percent(judge.revocation_case_share)}</td>
                          <td>{percent(judge.warrant_case_share)}</td>
                          <td>{numberOrDash(judge.avg_days_to_judgment, " days")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p>No case-level judge data is available.</p>}
            </article>
            <article className="panel">
              <h2>Cases By Judge</h2>
              <p className="chart-note">Case-level count by initial judicial officer. This is case-mix context, not a sentence severity ranking.</p>
              <Plot title="judge-case-counts" data={[{ type: "bar", orientation: "h", y: data.caseJudges.slice(0, 20).map((judge) => judge.judge_name).reverse(), x: data.caseJudges.slice(0, 20).map((judge) => judge.cases).reverse(), marker: { color: "#2563eb" }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 230, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
              <ChartTable rows={data.caseJudges as unknown as Array<Record<string, unknown>>} />
            </article>
            <article className="panel">
              <h2>Revocation And Warrant Context</h2>
              <p className="chart-note">Percent of each judge's case-level assignments with later revocation or warrant activity. Sample size matters.</p>
              <Plot title="judge-process-context" data={[
                { type: "bar", name: "Revocation activity", x: data.caseJudges.slice(0, 20).map((judge) => judge.judge_name), y: data.caseJudges.slice(0, 20).map((judge) => judge.revocation_case_share), marker: { color: "#be123c" }, hovertemplate: "%{x}<br>%{y}% cases<extra></extra>" },
                { type: "bar", name: "Warrant activity", x: data.caseJudges.slice(0, 20).map((judge) => judge.judge_name), y: data.caseJudges.slice(0, 20).map((judge) => judge.warrant_case_share), marker: { color: "#b45309" }, hovertemplate: "%{x}<br>%{y}% cases<extra></extra>" }
              ]} layout={{ margin: { l: 82, r: 24, t: 20, b: 150 }, barmode: "group", xaxis: { title: { text: "Initial judicial officer", standoff: 16 }, tickangle: -35, automargin: true }, yaxis: { title: { text: "% of cases", standoff: 14 }, ticksuffix: "%", automargin: true }, legend: { orientation: "h" } }} />
              <ChartTable rows={data.caseJudges.map((judge) => ({ judge: judge.judge_name, cases: judge.cases, "revocation activity (%)": judge.revocation_case_share, "warrant activity (%)": judge.warrant_case_share }))} />
              <ChartEvidence unit="case-level judge assignments" denominator="cases assigned to each judge" source="felony case export" caveat="District baseline is visible in the small-multiple panels above" />
            </article>
            <article className="panel">
              <h2>Case Mix By Judge</h2>
              <p className="chart-note">Top judge/category combinations by case count. Differences can reflect case assignment mix, not judicial behavior.</p>
              <Plot title="judge-case-mix" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.caseMixByJudge.slice(0, 25).map((row) => `${row.judge} / ${labelize(row.category)}`).reverse(), x: data.caseContextCharts.caseMixByJudge.slice(0, 25).map((row) => row.cases).reverse(), marker: { color: "#047857" }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 320, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
              <ChartTable rows={data.caseContextCharts.caseMixByJudge.map((row) => ({ judge: row.judge, category: labelize(row.category), cases: row.cases }))} />
            </article>
            <article className="panel">
              <h2>{selectedJudge || "Judge"} County Mix</h2>
              <p className="chart-note">Case-level county distribution for the selected initial judicial officer.</p>
              <Plot title="selected-judge-county-mix" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.caseCountyByJudge.filter((row) => row.judge === selectedJudge).map((row) => row.county).reverse(), x: data.caseContextCharts.caseCountyByJudge.filter((row) => row.judge === selectedJudge).map((row) => row.cases).reverse(), marker: { color: "#2563eb" }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 180, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
              <ChartTable rows={data.caseContextCharts.caseCountyByJudge.filter((row) => row.judge === selectedJudge).map((row) => ({ judge: row.judge, county: row.county, cases: row.cases }))} />
            </article>
            <article className="panel">
              <h2>{selectedJudge || "Judge"} Charge-Level Mix</h2>
              <p className="chart-note">Case-level primary charge severity for the selected initial judicial officer.</p>
              <Plot title="selected-judge-charge-level-mix" data={[{ type: "bar", x: data.caseContextCharts.caseChargeLevelByJudge.filter((row) => row.judge === selectedJudge).map((row) => row.level), y: data.caseContextCharts.caseChargeLevelByJudge.filter((row) => row.judge === selectedJudge).map((row) => row.cases), marker: { color: "#7c3aed" }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ xaxis: { title: { text: "Primary charge level", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true } }} />
              <ChartTable rows={data.caseContextCharts.caseChargeLevelByJudge.filter((row) => row.judge === selectedJudge).map((row) => ({ judge: row.judge, level: row.level, cases: row.cases }))} />
            </article>
            <article className="panel">
              <h2>{selectedJudge || "Judge"} Outcome Buckets</h2>
              <p className="chart-note">Case outcome buckets for the selected initial judicial officer.</p>
              <Plot title="selected-judge-outcomes" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.caseOutcomeByJudge.filter((row) => row.judge === selectedJudge).map((row) => labelize(row.outcome)).reverse(), x: data.caseContextCharts.caseOutcomeByJudge.filter((row) => row.judge === selectedJudge).map((row) => row.cases).reverse(), marker: { color: "#be123c" }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 260, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
              <ChartTable rows={data.caseContextCharts.caseOutcomeByJudge.filter((row) => row.judge === selectedJudge).map((row) => ({ judge: row.judge, outcome: labelize(row.outcome), cases: row.cases }))} />
            </article>
            <article className="panel">
              <h2>Selected Judge Vs District Mix</h2>
              <p className="chart-note">Positive values mean the selected judge's case mix has a higher share of that category than the district-wide share. This is case mix, not performance.</p>
              <Plot title="selected-judge-vs-district-mix" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.judgeVsDistrictMix.filter((row) => row.judge === selectedJudge).slice(0, 12).map((row) => labelize(row.category)).reverse(), x: data.caseContextCharts.judgeVsDistrictMix.filter((row) => row.judge === selectedJudge).slice(0, 12).map((row) => row.deltaPct).reverse(), marker: { color: "#64748b" }, hovertemplate: "%{y}<br>%{x}% point difference<extra></extra>" }]} layout={{ margin: { l: 220, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Percentage-point difference", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
              <ChartTable rows={data.caseContextCharts.judgeVsDistrictMix.filter((row) => row.judge === selectedJudge).map((row) => ({ judge: row.judge, category: labelize(row.category), "judge share": row.judgeShare, "district share": row.districtShare, "delta pct": row.deltaPct }))} />
            </article>
          </section>
        </section>
      )}

      {activeTab === "financial" && (
        <FinancialPreviewSection
          financial={data.financialPreview}
          filters={financialFilters}
          filteredRows={filteredFinancialRows}
          counties={financialCounties}
          judges={financialJudges}
          categories={financialCategories}
          levels={financialLevels}
          updateFilter={updateFinancialFilter}
          clearFilters={() => setFinancialFilters(initialFinancialFilters)}
        />
      )}

      {activeTab === "conditions" && (
        <section className="chart-grid">
          <article className="panel chart-grid-wide">
            <div className="panel-header">
              <div>
                <h2>Condition Display</h2>
                <p>Percent view uses unique cases as the denominator, so a case is counted once even if it has multiple matching sentence events.</p>
              </div>
              <div className="segmented-control" aria-label="Condition display mode">
                <button className={conditionDisplay === "events" ? "active" : ""} onClick={() => setConditionDisplay("events")}>Absolute numbers</button>
                <button className={conditionDisplay === "cases" ? "active" : ""} onClick={() => setConditionDisplay("cases")}>% of cases</button>
              </div>
            </div>
          </article>
          <article className="panel">
            <h2>Most Common Parsed Conditions</h2>
            <Plot title="condition-titles" data={[{ type: "bar", orientation: "h", y: conditionTitleRows.map((d) => d.label).reverse(), x: conditionTitleRows.map((d) => d.metricValue).reverse(), marker: { color: "#7c3aed" }, hovertemplate: `%{y}<br>${conditionMetricLabel(conditionDisplay)}: %{x}${conditionDisplay === "cases" ? "%" : ""}<extra></extra>` }]} layout={{ margin: { l: 270, r: 24, t: 20, b: 74 }, xaxis: { title: { text: conditionMetricLabel(conditionDisplay), standoff: 14 }, ticksuffix: conditionDisplay === "cases" ? "%" : "", automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
              if (typeof value === "string") {
                setActiveTab("search");
                applyFilters({ ...filters, conditionTitle: value });
              }
            }} />
            <ChartTable rows={conditionTableRows(data.charts.conditionTitles)} columns={["condition", "sentence events", "cases with condition", "% of cases"]} barColumn="sentence events" color={colors.condition} />
            <ChartEvidence unit="parsed sentence condition rows and unique cases" denominator={conditionDisplay === "cases" ? "unique cases with a condition flag" : "sentence events with parsed conditions"} source="parsed sentencing condition text" caveat="Raw condition text remains the review source of truth" />
          </article>
          <article className="panel">
            <h2>Condition Flags</h2>
            <Plot title="condition-flags" data={[{ type: "bar", orientation: "h", y: conditionFlagRows.map((d) => d.label).reverse(), x: conditionFlagRows.map((d) => d.metricValue).reverse(), marker: { color: "#b45309" }, hovertemplate: `%{y}<br>${conditionMetricLabel(conditionDisplay)}: %{x}${conditionDisplay === "cases" ? "%" : ""}<extra></extra>` }]} layout={{ margin: { l: 230, r: 24, t: 20, b: 74 }, xaxis: { title: { text: conditionMetricLabel(conditionDisplay), standoff: 14 }, ticksuffix: conditionDisplay === "cases" ? "%" : "", automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
              if (typeof value === "string") {
                const flag = Object.keys(flagLabels).find((key) => key.replace("condition_", "") === value);
                if (flag) {
                  setActiveTab("search");
                  applyFilters({ ...filters, conditionFlag: flag });
                }
              }
            }} />
            <ChartTable rows={conditionTableRows(data.charts.conditionFlags)} columns={["condition", "sentence events", "cases with condition", "% of cases"]} barColumn="sentence events" color={colors.procedure} />
            <ChartEvidence unit="condition flags" denominator="unique cases for percent view; sentence events for absolute view" source="parsed condition flags" caveat="Flags are convenience fields derived from text" />
          </article>
          <article className="panel">
            <h2>Condition Ranked Evidence Table</h2>
            <p className="chart-note">Precise condition comparison with absolute sentence events, unique cases, and percent-of-case denominator in one dense display.</p>
            <DenseComparisonTable rows={conditionTableRows(data.charts.conditionTitles).slice(0, 18)} labelKey="condition" valueKey="sentence events" percentKey="% of cases" color={colors.condition} />
          </article>
          <article className="panel">
            <h2>Condition Treemap Supplement</h2>
            <ConditionTreemap rows={conditionTitleRows} display={conditionDisplay} />
            <ChartTable rows={conditionTableRows(data.charts.conditionTitles)} columns={["condition", "sentence events", "cases with condition", "% of cases"]} barColumn="sentence events" color={colors.condition} />
          </article>
          <article className="panel chart-grid-wide">
            <h2>Condition Small Multiples By Offense</h2>
            <p className="chart-note">Each offense panel shows the top parsed condition flags and their within-category case percentages.</p>
            <div className="small-multiple-grid">
              {[...new Set(data.caseContextCharts.conditionByOffenseCategory.map((row) => row.category))].slice(0, 8).map((category) => (
                <div className="small-multiple" key={category}>
                  <h3>{labelize(category)}</h3>
                  {data.caseContextCharts.conditionByOffenseCategory.filter((row) => row.category === category).slice(0, 4).map((row) => (
                    <RangeRow key={`${category}-${row.flag}`} label={conditionFlagLabel(row.flag)} value={row.casePercent} color={colors.condition} />
                  ))}
                </div>
              ))}
            </div>
          </article>
          <article className="panel chart-grid-wide">
            <h2>Condition Small Multiples By Charge Level</h2>
            <p className="chart-note">Same condition comparison structure repeated by primary charge level.</p>
            <div className="small-multiple-grid">
              {[...new Set(data.caseContextCharts.conditionByChargeLevel.map((row) => row.level))].slice(0, 8).map((level) => (
                <div className="small-multiple" key={level}>
                  <h3>{level}</h3>
                  {data.caseContextCharts.conditionByChargeLevel.filter((row) => row.level === level).slice(0, 4).map((row) => (
                    <RangeRow key={`${level}-${row.flag}`} label={conditionFlagLabel(row.flag)} value={row.casePercent} color={colors.procedure} />
                  ))}
                </div>
              ))}
            </div>
          </article>
          <article className="panel">
            <h2>Conditions By Offense Category</h2>
            <p className="chart-note">Top condition flags within primary offense categories. Percent is unique cases inside each category.</p>
            <Plot title="conditions-by-offense-category" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.conditionByOffenseCategory.slice(0, 24).map((row) => `${labelize(row.category)} / ${conditionFlagLabel(row.flag)}`).reverse(), x: data.caseContextCharts.conditionByOffenseCategory.slice(0, 24).map((row) => row.casePercent ?? 0).reverse(), marker: { color: "#047857" }, hovertemplate: "%{y}<br>%{x}% of cases<extra></extra>" }]} layout={{ margin: { l: 320, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "% of cases in category", standoff: 14 }, ticksuffix: "%", automargin: true }, yaxis: { automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.conditionByOffenseCategory.map((row) => ({ category: labelize(row.category), condition: conditionFlagLabel(row.flag), "sentence events": row.sentenceEvents, "case count": row.caseCount, "% of cases": row.casePercent }))} />
          </article>
          <article className="panel">
            <h2>Conditions By Charge Level</h2>
            <p className="chart-note">Top condition flags within primary charge levels. Percent is unique cases inside each level.</p>
            <Plot title="conditions-by-charge-level" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.conditionByChargeLevel.slice(0, 24).map((row) => `${row.level} / ${conditionFlagLabel(row.flag)}`).reverse(), x: data.caseContextCharts.conditionByChargeLevel.slice(0, 24).map((row) => row.casePercent ?? 0).reverse(), marker: { color: "#b45309" }, hovertemplate: "%{y}<br>%{x}% of cases<extra></extra>" }]} layout={{ margin: { l: 280, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "% of cases in level", standoff: 14 }, ticksuffix: "%", automargin: true }, yaxis: { automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.conditionByChargeLevel.map((row) => ({ level: row.level, condition: conditionFlagLabel(row.flag), "sentence events": row.sentenceEvents, "case count": row.caseCount, "% of cases": row.casePercent }))} />
          </article>
        </section>
      )}

      {activeTab === "trends" && (
        <section className="chart-grid">
          <article className="panel">
            <h2>Events Over Time</h2>
            <p className="chart-note">Sentence-event volume by month. {endpointText(data.charts.monthlyEvents, (row) => row.events, "Endpoints")}.</p>
            <Plot title="monthly-events" description="Monthly sentence-event volume with extraction and filing-window context." data={[{ type: "scatter", mode: "lines", x: data.charts.monthlyEvents.map((d) => d.month), y: data.charts.monthlyEvents.map((d) => d.events), line: { color: colors.sentence }, hovertemplate: "%{x}<br>%{y:,} sentence events<extra></extra>" }]} layout={{ yaxis: { title: { text: "Sentence events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Sentence event month", standoff: 14 }, automargin: true }, shapes: [{ type: "rect", xref: "x", yref: "paper", x0: "2018-01", x1: "2022-12", y0: 0, y1: 1, fillcolor: "rgba(15, 118, 110, 0.08)", line: { width: 0 }, layer: "below" }], annotations: [{ x: "2020-07", y: 1, xref: "x", yref: "paper", text: "2018-2022 filing window", showarrow: false, font: { size: 11, color: colors.neutral } }] }} />
            <ChartTable rows={data.charts.monthlyEvents} columns={["month", "events", "avgConfinement", "avgProbation"]} barColumn="events" color={colors.sentence} />
          </article>
          <article className="panel">
            <h2>Procedural Events Over Time</h2>
            <p className="chart-note">Monthly docket/procedural event rows from the felony case export.</p>
            <Plot title="procedural-events-over-time" description="Monthly procedural event rows from case docket data." data={[{ type: "scatter", mode: "lines", x: data.caseContextCharts.monthlyEvents.map((d) => d.month), y: data.caseContextCharts.monthlyEvents.map((d) => d.events), line: { color: colors.procedure }, hovertemplate: "%{x}<br>%{y:,} procedural events<extra></extra>" }]} layout={{ yaxis: { title: { text: "Procedural events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Event month", standoff: 14 }, automargin: true }, shapes: [{ type: "rect", xref: "x", yref: "paper", x0: "2018-01", x1: "2022-12", y0: 0, y1: 1, fillcolor: "rgba(15, 118, 110, 0.08)", line: { width: 0 }, layer: "below" }] }} />
            <ChartTable rows={data.caseContextCharts.monthlyEvents} barColumn="events" color={colors.procedure} />
            <ChartEvidence unit="docket/procedural event rows" denominator="monthly event rows; not cases" source="felony case export" caveat="Post-filing activity extends beyond the filing window" />
          </article>
          <article className="panel">
            <h2>Normalized Procedural Rates</h2>
            <p className="chart-note">Event counts normalized by same-month filings so volume spikes are easier to compare with case intake.</p>
            <Plot title="normalized-procedural-rates" description="Normalized monthly procedural, warrant, and revocation rates per 100 same-month filed cases." data={[
              { type: "scatter", mode: "lines", name: "Events per filed case", x: proceduralRates.map((row) => row.month), y: proceduralRates.map((row) => row.eventsPerFiledCase), line: { color: colors.procedure }, hovertemplate: "%{x}<br>%{y} events per filed case<extra></extra>" },
              { type: "scatter", mode: "lines", name: "Warrants per 100 filed", x: warrantRates.map((row) => row.month), y: warrantRates.map((row) => row.warrantsPer100Filed), line: { color: colors.warning }, hovertemplate: "%{x}<br>%{y} warrants per 100 filed cases<extra></extra>" },
              { type: "scatter", mode: "lines", name: "Revocations per 100 filed", x: revocationRates.map((row) => row.month), y: revocationRates.map((row) => row.revocationsPer100Filed), line: { color: colors.case }, hovertemplate: "%{x}<br>%{y} revocations per 100 filed cases<extra></extra>" }
            ]} layout={{ yaxis: { title: { text: "Normalized rate", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={proceduralRates.map((row, index) => ({ month: row.month, "events/filed case": row.eventsPerFiledCase, "warrants/100 filed": warrantRates[index]?.warrantsPer100Filed, "revocations/100 filed": revocationRates[index]?.revocationsPer100Filed, "filed cases": row.cases }))} columns={["month", "events/filed case", "warrants/100 filed", "revocations/100 filed", "filed cases"]} />
            <ChartEvidence unit="normalized rates" denominator="same-month case filings" source="felony case export" caveat="Rates use filing month as a comparison denominator, not causal exposure time" />
          </article>
          <article className="panel">
            <h2>Procedural Event Buckets</h2>
            <p className="chart-note">Stacked monthly docket events by broad procedural bucket.</p>
            <Plot title="procedural-event-buckets-stacked" data={eventBuckets.map((bucket, index) => ({
              type: "bar",
              name: labelize(bucket),
              x: data.caseContextCharts.monthlyEvents.map((d) => d.month),
              y: data.caseContextCharts.monthlyEvents.map((monthRow) => data.caseContextCharts.monthlyEventBuckets.find((row) => row.month === monthRow.month && row.bucket === bucket)?.events ?? 0),
              marker: { color: eventBucketColors[index % eventBucketColors.length] },
              hovertemplate: "%{x}<br>%{y:,} events<extra></extra>"
            }))} layout={{ barmode: "stack", yaxis: { title: { text: "Procedural events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Event month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={data.caseContextCharts.monthlyEventBuckets} />
          </article>
          <article className="panel chart-grid-wide">
            <h2>Procedural Buckets As Small Multiples</h2>
            <p className="chart-note">Each bucket uses the same row structure so shape and timing are comparable without stacked-bar occlusion.</p>
            <div className="small-multiple-grid">
              {eventBuckets.map((bucket, index) => {
                const rows = data.caseContextCharts.monthlyEvents.map((monthRow) => data.caseContextCharts.monthlyEventBuckets.find((row) => row.month === monthRow.month && row.bucket === bucket)?.events ?? 0);
                return (
                  <div className="small-multiple" key={bucket}>
                    <h3>{labelize(bucket)}</h3>
                    <Sparkline values={rows} color={eventBucketColors[index % eventBucketColors.length]} label={`${labelize(bucket)} monthly sparkline`} />
                    <span>{compactNumber(sumValues(rows, (value) => value))} events</span>
                  </div>
                );
              })}
            </div>
            <TrendRowsTable rows={eventBuckets.map((bucket, index) => {
              const values = data.caseContextCharts.monthlyEvents.map((monthRow) => data.caseContextCharts.monthlyEventBuckets.find((row) => row.month === monthRow.month && row.bucket === bucket)?.events ?? 0);
              return { label: labelize(bucket), values, total: sumValues(values, (value) => value), color: eventBucketColors[index % eventBucketColors.length] };
            })} />
          </article>
          <article className="panel">
            <h2>Most Common Procedural Event Titles</h2>
            <p className="chart-note">Top docket event titles across the felony case export.</p>
            <Plot title="top-procedural-event-titles" description="Top procedural event title counts." data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.topEventTitles.slice(0, 15).map((d) => d.label).reverse(), customdata: data.caseContextCharts.topEventTitles.slice(0, 15).map((d) => d.label).reverse(), x: data.caseContextCharts.topEventTitles.slice(0, 15).map((d) => d.value).reverse(), marker: { color: colors.procedure }, hovertemplate: "%{y}<br>%{x:,} events<extra></extra>" }]} layout={{ margin: { l: 280, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Procedural events", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
              if (typeof value === "string") {
                setActiveTab("search");
                switchSearchMode("case");
                applyCaseFilters({ ...caseFilters, query: value });
              }
            }} />
            <ChartTable rows={data.caseContextCharts.topEventTitles} barColumn="value" color={colors.procedure} />
          </article>
          <article className="panel">
            <h2>Warrant Events Over Time</h2>
            <p className="chart-note">Monthly count of docket events flagged as warrant-related.</p>
            <Plot title="warrant-events-over-time" description="Monthly warrant event counts." data={[{ type: "scatter", mode: "lines", x: data.caseContextCharts.monthlyWarrants.map((d) => d.month), y: data.caseContextCharts.monthlyWarrants.map((d) => d.warrants), line: { color: colors.warning }, hovertemplate: "%{x}<br>%{y:,} warrant events<extra></extra>" }]} layout={{ yaxis: { title: { text: "Warrant events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Event month", standoff: 14 }, automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.monthlyWarrants} barColumn="warrants" color={colors.warning} />
          </article>
          <article className="panel">
            <h2>Revocation And Probation Events</h2>
            <p className="chart-note">Monthly procedural event counts flagged as revocation or probation-related.</p>
            <Plot title="revocation-probation-events" data={[
              { type: "scatter", mode: "lines", name: "Revocation events", x: data.caseContextCharts.monthlyRevocationEvents.map((d) => d.month), y: data.caseContextCharts.monthlyRevocationEvents.map((d) => d.revocationEvents), line: { color: colors.warning }, hovertemplate: "%{x}<br>%{y:,} revocation events<extra></extra>" },
              { type: "scatter", mode: "lines", name: "Probation events", x: data.caseContextCharts.monthlyRevocationEvents.map((d) => d.month), y: data.caseContextCharts.monthlyRevocationEvents.map((d) => d.probationEvents), line: { color: colors.case }, hovertemplate: "%{x}<br>%{y:,} probation events<extra></extra>" }
            ]} layout={{ yaxis: { title: { text: "Procedural events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Event month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={data.caseContextCharts.monthlyRevocationEvents} />
          </article>
          <article className="panel">
            <h2>Hearing Results</h2>
            <p className="chart-note">Most common populated hearing-result values in docket events.</p>
            <Plot title="hearing-results" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.hearingResults.slice(0, 15).map((d) => d.label).reverse(), x: data.caseContextCharts.hearingResults.slice(0, 15).map((d) => d.value).reverse(), marker: { color: "#64748b" }, hovertemplate: "%{y}<br>%{x:,} events<extra></extra>" }]} layout={{ margin: { l: 260, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Events", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.hearingResults} />
          </article>
          <article className="panel">
            <h2>Bond Events Over Time</h2>
            <p className="chart-note">Monthly docket event counts flagged as bond-related, including reduction and revocation flags where present.</p>
            <Plot title="bond-events-over-time" data={[
              { type: "scatter", mode: "lines", name: "Bond events", x: data.caseContextCharts.monthlyBondEvents.map((d) => d.month), y: data.caseContextCharts.monthlyBondEvents.map((d) => d.bondEvents), line: { color: colors.sentence }, hovertemplate: "%{x}<br>%{y:,} bond events<extra></extra>" },
              { type: "scatter", mode: "lines", name: "Bond reductions", x: data.caseContextCharts.monthlyBondEvents.map((d) => d.month), y: data.caseContextCharts.monthlyBondEvents.map((d) => d.bondReductions), line: { color: colors.case }, hovertemplate: "%{x}<br>%{y:,} reductions<extra></extra>" },
              { type: "scatter", mode: "lines", name: "Bond revocations", x: data.caseContextCharts.monthlyBondEvents.map((d) => d.month), y: data.caseContextCharts.monthlyBondEvents.map((d) => d.bondRevocations), line: { color: colors.warning }, hovertemplate: "%{x}<br>%{y:,} revocations<extra></extra>" }
            ]} layout={{ yaxis: { title: { text: "Procedural events", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Event month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={data.caseContextCharts.monthlyBondEvents} />
          </article>
          <article className="panel chart-grid-wide">
            <h2>Filing-Year Cohorts</h2>
            <p className="chart-note">{caseDataLoading ? "Loading case cohorts..." : "Cohorts group cases by filing year, then compare outcome and process rates inside each filing year."}</p>
            <Plot title="filing-year-cohorts" description="Filing-year cohorts showing guilty plea, revocation, and warrant shares." data={[
              { type: "scatter", mode: "lines+markers", name: "Guilty plea/disposition", x: caseCohorts.map((row) => row.year), y: caseCohorts.map((row) => row.guiltyShare), line: { color: colors.case }, hovertemplate: "%{x}<br>%{y}% cases<extra></extra>" },
              { type: "scatter", mode: "lines+markers", name: "Revocation activity", x: caseCohorts.map((row) => row.year), y: caseCohorts.map((row) => row.revocationShare), line: { color: colors.warning }, hovertemplate: "%{x}<br>%{y}% cases<extra></extra>" },
              { type: "scatter", mode: "lines+markers", name: "Warrant activity", x: caseCohorts.map((row) => row.year), y: caseCohorts.map((row) => row.warrantShare), line: { color: colors.procedure }, hovertemplate: "%{x}<br>%{y}% cases<extra></extra>" }
            ]} layout={{ yaxis: { title: { text: "% of filed-year cases", standoff: 14 }, ticksuffix: "%", automargin: true }, xaxis: { title: { text: "Filed year", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={caseCohorts.map((row) => ({ year: row.year, cases: row.cases, "guilty %": row.guiltyShare, "revocation %": row.revocationShare, "warrant %": row.warrantShare, "median judgment days": row.medianJudgmentDays, "median disposition days": row.medianDispositionDays }))} columns={["year", "cases", "guilty %", "revocation %", "warrant %", "median judgment days"]} barColumn="cases" color={colors.case} />
            <ChartEvidence unit="case profiles grouped by filing year" denominator="cases filed in each year" source="case-search snapshot" caveat="Later procedural events can occur after the filing year" />
          </article>
          <article className="panel chart-grid-wide">
            <h2>Procedural Event Search</h2>
            <p className="chart-note">API-backed strict pagination for raw docket/event rows. Use this for targeted review rather than loading the full event export into the browser.</p>
            <div className="inline-form">
              <label>Search<input id="event-search-query" name="event-search-query" value={eventSearchFilters.q} onChange={(event) => updateEventSearch("q", event.target.value)} placeholder="Case, title, or detail" /></label>
              <label>From<input id="event-search-from" name="event-search-from" type="date" value={eventSearchFilters.eventFrom} onChange={(event) => updateEventSearch("eventFrom", event.target.value)} /></label>
              <label>To<input id="event-search-to" name="event-search-to" type="date" value={eventSearchFilters.eventTo} onChange={(event) => updateEventSearch("eventTo", event.target.value)} /></label>
              <label>Bucket<select id="event-search-bucket" name="event-search-bucket" value={eventSearchFilters.eventBucket} onChange={(event) => updateEventSearch("eventBucket", event.target.value)}><option value="">Any</option>{data.caseContextCharts.eventBuckets.map((bucket) => <option key={bucket.label} value={bucket.label}>{labelize(bucket.label)}</option>)}</select></label>
              <label>Flag<select id="event-search-flag" name="event-search-flag" value={eventSearchFilters.eventFlag} onChange={(event) => updateEventSearch("eventFlag", event.target.value)}><option value="">Any</option>{Object.entries(eventFlagLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
              <button onClick={() => runEventSearch(0)}>{eventSearchLoading ? "Searching..." : "Search events"}</button>
            </div>
            {eventSearchPayload ? (
              <>
                <p className="chart-note">{String(eventSearchPayload.meta.total ?? 0)} matching procedural events. Showing {String(eventSearchPayload.meta.sampleSize ?? 0)} rows from offset {String(eventSearchPayload.meta.offset ?? 0)}.</p>
                <div className="preset-row">
                  <button className="secondary" disabled={Number(eventSearchPayload.meta.offset ?? 0) <= 0} onClick={() => runEventSearch(Math.max(0, Number(eventSearchPayload.meta.offset ?? 0) - 25))}>Previous page</button>
                  <button className="secondary" disabled={Number(eventSearchPayload.meta.offset ?? 0) + 25 >= Number(eventSearchPayload.meta.total ?? 0)} onClick={() => runEventSearch(Number(eventSearchPayload.meta.offset ?? 0) + 25)}>Next page</button>
                </div>
                <div className="table-wrap compact">
                  <table>
                    <thead><tr><th>Case</th><th>Date</th><th>Title</th><th>Bucket</th><th>Judge</th><th>Detail</th></tr></thead>
                    <tbody>
                      {eventSearchPayload.results.map((event, index) => (
                        <tr key={index}>
                          <td>{String(event.case_number ?? "Unavailable")}</td>
                          <td>{shortDate(String(event.event_date ?? ""))}</td>
                          <td>{String(event.event_title ?? "Unavailable")}</td>
                          <td>{labelize(String(event.event_type_bucket ?? ""))}</td>
                          <td>{String(event.judicial_officer ?? "Unavailable")}</td>
                          <td>{event.event_detail ? <details><summary>Event detail</summary><p>{String(event.event_detail)}</p></details> : "Unavailable"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </article>
          <article className="panel">
            <h2>Procedural Bucket Definitions</h2>
            <p>Hearings are scheduled court appearances. Orders are court-issued directives. Service documents show notice or service activity. Dispositions close or resolve a case stage. Judgments are sentence/disposition documents. Motions are party requests. Filing/other captures docket entries that do not fit those higher-signal buckets.</p>
          </article>
          <article className="panel">
            <h2>Confinement And Probation</h2>
            <p className="chart-note">Each point is one sentence event. X-axis is confinement imposed in months; Y-axis is probation ordered in months.</p>
            <Plot title="confinement-probation-scatter" data={[{ type: "scattergl", mode: "markers", x: data.charts.scatter.map((d) => d.confinement_months_imposed), y: data.charts.scatter.map((d) => d.probation_months), marker: { color: "#047857", opacity: 0.55 }, hovertemplate: "Confinement: %{x} months<br>Probation: %{y} months<extra></extra>" }]} layout={{ margin: { l: 100, r: 24, t: 20, b: 98 }, xaxis: { title: { text: "Confinement imposed (months)", standoff: 16 }, automargin: true }, yaxis: { title: { text: "Probation ordered (months)", standoff: 18 }, automargin: true } }} />
            <ChartTable rows={data.charts.scatter} />
          </article>
          <article className="panel">
            <h2>Average Sentence Length Over Time</h2>
            <p className="chart-note">Both lines are monthly averages measured in months. Dashed lines show a simple linear trend across available monthly averages.</p>
            <Plot
              title="average-length-over-time"
              data={[
                { type: "scatter", mode: "lines", name: "Avg confinement months", x: data.charts.monthlyEvents.map((d) => d.month), y: data.charts.monthlyEvents.map((d) => d.avgConfinement), line: { color: colors.case }, hovertemplate: "%{x}<br>Avg confinement: %{y} months<extra></extra>" },
                { type: "scatter", mode: "lines", name: "Confinement trend", x: data.charts.monthlyEvents.map((d) => d.month), y: avgConfinementTrend, line: { color: "#064e3b", dash: "dash", width: 2 }, hovertemplate: "%{x}<br>Confinement trend: %{y} months<extra></extra>" },
                { type: "scatter", mode: "lines", name: "Avg probation months", x: data.charts.monthlyEvents.map((d) => d.month), y: data.charts.monthlyEvents.map((d) => d.avgProbation), line: { color: colors.condition }, hovertemplate: "%{x}<br>Avg probation: %{y} months<extra></extra>" },
                { type: "scatter", mode: "lines", name: "Probation trend", x: data.charts.monthlyEvents.map((d) => d.month), y: avgProbationTrend, line: { color: "#581c87", dash: "dash", width: 2 }, hovertemplate: "%{x}<br>Probation trend: %{y} months<extra></extra>" }
              ]}
              layout={{ margin: { l: 96, r: 24, t: 20, b: 98 }, yaxis: { title: { text: "Average length (months)", standoff: 18 }, automargin: true }, xaxis: { title: { text: "Sentence event month", standoff: 16 }, automargin: true }, legend: { orientation: "h" }, annotations: [{ x: data.charts.monthlyEvents.at(-1)?.month, y: data.charts.monthlyEvents.at(-1)?.avgConfinement, text: "confinement", showarrow: false, xanchor: "left", font: { color: colors.case, size: 11 } }, { x: data.charts.monthlyEvents.at(-1)?.month, y: data.charts.monthlyEvents.at(-1)?.avgProbation, text: "probation", showarrow: false, xanchor: "left", font: { color: colors.condition, size: 11 } }] }}
            />
            <ChartTable rows={data.charts.monthlyEvents} />
          </article>
          <article className="panel">
            <h2>Sentence Length By Offense Category</h2>
            <p className="chart-note">Sentence-event averages joined to case-level primary offense category. Units are months.</p>
            <Plot title="sentence-length-by-offense-category" data={[
              { type: "bar", name: "Avg confinement", x: data.caseContextCharts.sentencingByOffenseCategory.map((row) => labelize(row.category)), y: data.caseContextCharts.sentencingByOffenseCategory.map((row) => row.avgConfinementMonths), marker: { color: "#047857" }, hovertemplate: "%{x}<br>%{y} months<extra></extra>" },
              { type: "bar", name: "Avg probation", x: data.caseContextCharts.sentencingByOffenseCategory.map((row) => labelize(row.category)), y: data.caseContextCharts.sentencingByOffenseCategory.map((row) => row.avgProbationMonths), marker: { color: "#9333ea" }, hovertemplate: "%{x}<br>%{y} months<extra></extra>" }
            ]} layout={{ barmode: "group", margin: { l: 82, r: 24, t: 20, b: 130 }, xaxis: { title: { text: "Primary offense category", standoff: 16 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Average sentence length (months)", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={data.caseContextCharts.sentencingByOffenseCategory.map((row) => ({ category: labelize(row.category), "sentence events": row.sentenceEvents, "avg confinement months": row.avgConfinementMonths, "avg probation months": row.avgProbationMonths }))} />
          </article>
          <article className="panel">
            <h2>Sentence Length By Charge Level</h2>
            <p className="chart-note">Sentence-event averages joined to case-level primary charge level. Units are months.</p>
            <Plot title="sentence-length-by-charge-level" data={[
              { type: "bar", name: "Avg confinement", x: data.caseContextCharts.sentencingByChargeLevel.map((row) => row.level), y: data.caseContextCharts.sentencingByChargeLevel.map((row) => row.avgConfinementMonths), marker: { color: "#047857" }, hovertemplate: "%{x}<br>%{y} months<extra></extra>" },
              { type: "bar", name: "Avg probation", x: data.caseContextCharts.sentencingByChargeLevel.map((row) => row.level), y: data.caseContextCharts.sentencingByChargeLevel.map((row) => row.avgProbationMonths), marker: { color: "#9333ea" }, hovertemplate: "%{x}<br>%{y} months<extra></extra>" }
            ]} layout={{ barmode: "group", xaxis: { title: { text: "Primary charge level", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Average sentence length (months)", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
            <ChartTable rows={data.caseContextCharts.sentencingByChargeLevel.map((row) => ({ level: row.level, "sentence events": row.sentenceEvents, "avg confinement months": row.avgConfinementMonths, "avg probation months": row.avgProbationMonths }))} />
          </article>
          <article className="panel">
            <h2>Credit Served And Confinement</h2>
            <p className="chart-note">Each point is one sentence event. X-axis is credit for time served in days; Y-axis is confinement imposed in months.</p>
            <Plot title="credit-confinement-scatter" data={[{ type: "scattergl", mode: "markers", x: data.charts.scatter.map((d) => d.credit_for_time_served_days), y: data.charts.scatter.map((d) => d.confinement_months_imposed), marker: { color: "#b45309", opacity: 0.55 }, hovertemplate: "Credit served: %{x} days<br>Confinement: %{y} months<extra></extra>" }]} layout={{ margin: { l: 100, r: 24, t: 20, b: 98 }, xaxis: { title: { text: "Credit for time served (days)", standoff: 16 }, automargin: true }, yaxis: { title: { text: "Confinement imposed (months)", standoff: 18 }, automargin: true } }} />
            <ChartTable rows={data.charts.scatter} />
          </article>
          <article className="panel">
            <h2>Revocation Share Over Time</h2>
            <p className="chart-note">Y-axis is the percentage of sentence events in that month marked as revocation.</p>
            <Plot title="revocation-share" data={[{ type: "scatter", mode: "lines+markers", x: data.charts.monthlyRevocations.map((d) => d.month), y: data.charts.monthlyRevocations.map((d) => d.revocationShare), line: { color: "#be123c" } }]} layout={{ margin: { l: 86, r: 24, t: 20, b: 88 }, yaxis: { title: "Revocation share (%)", automargin: true }, xaxis: { title: "Sentence event month", automargin: true } }} />
            <ChartTable rows={data.charts.monthlyRevocations.map((row) => ({ month: row.month, "sentence events": row.events, revocations: row.revocations, "revocation share (%)": row.revocationShare }))} />
          </article>
          <article className="panel">
            <h2>Revocation Activity By Offense Category</h2>
            <p className="chart-note">Case-level share of cases with later revocation activity by primary offense category.</p>
            <Plot title="revocation-by-offense-category" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.revocationByOffenseCategory.map((row) => labelize(row.category)).reverse(), x: data.caseContextCharts.revocationByOffenseCategory.map((row) => row.revocationCaseShare ?? 0).reverse(), marker: { color: "#be123c" }, hovertemplate: "%{y}<br>%{x}% of cases<extra></extra>" }]} layout={{ margin: { l: 220, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "% of cases with revocation activity", standoff: 14 }, ticksuffix: "%", automargin: true }, yaxis: { automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.revocationByOffenseCategory.map((row) => ({ category: labelize(row.category), cases: row.cases, "revocation activity (%)": row.revocationCaseShare }))} />
          </article>
        </section>
      )}

      {activeTab === "methodology" && (
        <section className="page-grid">
          <article className="panel prose">
            <h2>Methodology And Caveats</h2>
            {data.methodology.notes.map((note) => <p key={note}>{note}</p>)}
            <p>The original sentencing extract and the felony case export are related but not identical. Sentencing charts count sentence-event rows; case-context charts count case, charge, or procedural-event rows as labeled.</p>
            <p>A case can have multiple charges and many docket events. Final disposition and procedural event dates can extend beyond the 2018-2022 filing window because cases continue after filing.</p>
            <p>Case-level initial judicial officer is not automatically a sentencing judge. Event-level judge attribution is sparse and is labeled separately where used.</p>
            <p>Public snapshots omit defendant names, prosecutor names, defense attorney names, SID, law enforcement id, JustWare id, prosecutor file id, ZIP, city, birth year, and financial balances. Raw procedural event detail is kept out of initial public snapshots and loaded only through detail workflows.</p>
            <p>Amended judgments and revocations are represented as their own sentence events. They should be read chronologically inside a case instead of treated as automatic replacements for earlier events.</p>
            <p>Parsed conditions are convenience fields. The raw extracted text remains the source of truth for reviewing a condition.</p>
          </article>
          <section className="metrics">
            <MetricCard label="Missing initial judge" value={numberOrDash(data.caseDataQuality.checks.find((check) => check.check_name === "missing_initial_judicial_officer")?.affected_count)} note="Case profiles" />
            <MetricCard label="No charges parsed" value={numberOrDash(data.caseDataQuality.checks.find((check) => check.check_name === "case_profiles_without_charges")?.affected_count)} note="Case profiles" />
            <MetricCard label="No events parsed" value={numberOrDash(data.caseDataQuality.checks.find((check) => check.check_name === "case_profiles_without_events")?.affected_count)} note="Case profiles" />
            <MetricCard label="Missing case number" value={numberOrDash(data.caseDataQuality.checks.find((check) => check.check_name === "missing_case_number")?.affected_count)} note="Case profiles" />
            <MetricCard label="Missing filed/county" value={numberOrDash(data.caseDataQuality.auditIssues.find((issue) => issue.issue === "missing filed_date")?.affected_count ?? data.caseDataQuality.auditIssues.find((issue) => issue.issue === "missing county")?.affected_count)} note="Audit rows" />
            <MetricCard label="Source files" value={numberOrDash(data.summary.totalSourceFiles)} note="HTML source references" />
          </section>
          <article className="panel">
            <h2>Data Quality Checks</h2>
            <p className="chart-note">Imported audit and reconciliation checks from the felony case export. Counts are descriptive and should guide review, not imply data is unusable.</p>
            <div className="table-wrap compact">
              <table>
                <thead><tr><th>Check</th><th>Severity</th><th>Affected</th></tr></thead>
                <tbody>
                  {data.caseDataQuality.checks.map((check) => (
                    <tr key={check.check_name}><td>{labelize(check.check_name)}</td><td>{check.severity}</td><td>{check.affected_count.toLocaleString()}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
          <article className="panel">
            <h2>Audit Issues</h2>
            <Plot title="audit-issues" data={[{ type: "bar", orientation: "h", y: data.caseContextCharts.auditIssues.slice(0, 12).map((d) => d.label).reverse(), customdata: data.caseContextCharts.auditIssues.slice(0, 12).map((d) => d.label).reverse(), x: data.caseContextCharts.auditIssues.slice(0, 12).map((d) => d.value).reverse(), marker: { color: "#64748b" }, hovertemplate: "%{y}<br>%{x:,} rows<extra></extra>" }]} layout={{ margin: { l: 280, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Rows", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} onPointClick={(value) => {
              if (typeof value === "string") void loadQualityIssue(value, 0);
            }} />
            <ChartTable rows={data.caseDataQuality.auditIssues.map((issue) => ({ issue: issue.issue, severity: issue.severity, affected: issue.affected_count }))} />
          </article>
          <article className="panel">
            <h2>Audit Severity</h2>
            <p className="chart-note">Rows in `audit.csv` by severity label.</p>
            <Plot title="audit-severity" data={[{ type: "bar", x: data.caseContextCharts.auditSeverity.map((d) => labelize(d.label)), y: data.caseContextCharts.auditSeverity.map((d) => d.value), marker: { color: "#b45309" }, hovertemplate: "%{x}<br>%{y:,} rows<extra></extra>" }]} layout={{ xaxis: { title: { text: "Severity", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Audit rows", standoff: 14 }, automargin: true } }} />
            <ChartTable rows={data.caseContextCharts.auditSeverity.map((row) => ({ severity: row.label, rows: row.value }))} />
          </article>
          <article className="panel chart-grid-wide">
            <h2>Affected Cases By Audit Issue</h2>
            <p className="chart-note">Select an audit issue to request a paginated list of affected case numbers/source files from D1.</p>
            <div className="inline-form">
              <label>Audit issue<select id="quality-issue" name="quality-issue" value={qualityIssue} onChange={(event) => void loadQualityIssue(event.target.value, 0)}><option value="">Choose issue</option>{data.caseDataQuality.auditIssues.map((issue) => <option key={`${issue.issue}-${issue.severity}`} value={issue.issue}>{issue.issue}</option>)}</select></label>
              <button disabled={!qualityIssue || qualityLoading} onClick={() => void loadQualityIssue(qualityIssue, 0)}>{qualityLoading ? "Loading..." : "Load affected cases"}</button>
            </div>
            {qualityAffectedPayload ? (
              <>
                <p className="chart-note">{String(qualityAffectedPayload.meta.total ?? 0)} affected rows for `{qualityIssue}`. Showing {String(qualityAffectedPayload.meta.sampleSize ?? 0)} rows.</p>
                <div className="preset-row">
                  <button className="secondary" disabled={Number(qualityAffectedPayload.meta.offset ?? 0) <= 0} onClick={() => void loadQualityIssue(qualityIssue, Math.max(0, Number(qualityAffectedPayload.meta.offset ?? 0) - 25))}>Previous page</button>
                  <button className="secondary" disabled={Number(qualityAffectedPayload.meta.offset ?? 0) + 25 >= Number(qualityAffectedPayload.meta.total ?? 0)} onClick={() => void loadQualityIssue(qualityIssue, Number(qualityAffectedPayload.meta.offset ?? 0) + 25)}>Next page</button>
                </div>
                <div className="table-wrap compact">
                  <table>
                    <thead><tr><th>Case</th><th>Source file</th><th>Issue</th><th>Severity</th></tr></thead>
                    <tbody>{qualityAffectedPayload.affectedCases.map((row, index) => <tr key={index}><td>{String(row.case_number ?? "Unavailable")}</td><td>{String(row.source_file ?? "Unavailable")}</td><td>{String(row.issue ?? "Unavailable")}</td><td>{String(row.severity ?? "Unavailable")}</td></tr>)}</tbody>
                  </table>
                </div>
              </>
            ) : null}
          </article>
        </section>
      )}

      {activeTab === "dictionary" && (
        <section className="panel">
          <h2>Data Dictionary</h2>
          <dl className="dictionary">
            {Object.entries(data.dataDictionary).map(([field, description]) => (
              <div key={field}><dt>{field}</dt><dd>{description}</dd></div>
            ))}
          </dl>
        </section>
      )}

      {activeTab === "downloads" && (
        <section className="panel prose">
          <h2>Downloads And Source Traceability</h2>
          <p>Use the search export for filtered sentence-event data. Case and sentence detail endpoints preserve source file references and raw condition text for review workflows.</p>
          <a className="download-link" href="/data/search.json"><ExternalLink aria-hidden="true" /> Static sentence event snapshot</a>
          <a className="download-link" href="/data/data-dictionary.json"><ExternalLink aria-hidden="true" /> Data dictionary</a>
          <div>
            <h2>Embeddable Charts</h2>
            <p>Use an iframe with a tab query when embedding a beta chart view.</p>
            <code>{`<iframe src="https://scjd-beta.pages.dev/?tab=trends" title="SCJD sentencing trends"></iframe>`}</code>
          </div>
          <div>
            <h2>Public API</h2>
            <p>Available endpoints include `/api/summary`, `/api/search`, `/api/cases/:caseNumber`, `/api/sentence-events/:id`, `/api/judges`, and chart endpoints under `/api/charts/*`.</p>
          </div>
        </section>
      )}

      {activeTab === "about" && (
        <section className="panel prose">
          <h2>About And Feedback</h2>
          <p>This beta presents extracted sentencing data for review and analysis. It is designed for public interpretation while preserving source traceability for attorneys and researchers.</p>
          <p>For corrections, record the case number, sentence event title or id, source file, observed value, proposed correction, and the source text supporting the change. Corrections should be applied through the repeatable import or migration workflow described in `docs/OPERATIONS.md`.</p>
          <p>Judge analytics are intentionally labeled as pending because the current CSV does not include judge names.</p>
          <p>The trends page can include future annotation layers for policy changes, extraction changes, or court-process milestones. Current beta annotations are limited to methodology notes because no external event timeline has been provided.</p>
        </section>
      )}
    </main>
  );
}

function DetailPanel({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <section className="detail-panel" aria-label={title}>
      <div className="panel-header">
        <h2>{title}</h2>
        <button className="secondary" onClick={onClose}>Close</button>
      </div>
      {children}
    </section>
  );
}

function CaseDetail({ payload }: { payload: Record<string, unknown> }) {
  const caseRow = payload.case as Record<string, unknown>;
  const events = payload.events as Array<Record<string, unknown>>;
  return (
    <div className="prose">
      <p><strong>{String(caseRow.case_number)}</strong> has {events.length} sentence events shown chronologically.</p>
      <div className="table-wrap compact">
        <table>
          <thead><tr><th>Date</th><th>Title</th><th>Type</th><th>Confinement</th><th>Probation</th><th>Source</th></tr></thead>
          <tbody>
            {events.map((event) => (
              <tr key={String(event.sentence_event_id)}>
                <td>{String(event.sentence_event_date ?? "Unavailable")}</td>
                <td>{String(event.sentence_event_title ?? "Unavailable")}</td>
                <td>{String(event.sentence_type ?? "Unavailable")}</td>
                <td>{String(event.confinement_months_imposed ?? "Unavailable")}</td>
                <td>{String(event.probation_months ?? "Unavailable")}</td>
                <td>{String(event.source_file ?? "Unavailable")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CaseProfileDetail({ payload }: { payload: { profile: CaseSearchRow; charges: CaseChargeRow[]; events: CaseEventHighlight[]; quality: Array<Record<string, unknown>>; financial?: FinancialCaseRow } }) {
  const { profile, charges, events, quality, financial } = payload;
  const profileWarnings = [
    !profile.judicial_officer_initial ? "Missing initial judicial officer" : "",
    !charges.length ? "No parsed charge rows" : "",
    !events.length ? "No procedural event rows available in detail snapshot" : ""
  ].filter(Boolean);
  const lifecycle = [
    { label: "Filed", value: profile.filed_date },
    { label: "First plea", value: profile.first_plea_date },
    { label: "First judgment", value: profile.first_judgment_date },
    { label: "First revocation", value: profile.first_revocation_event_date },
    { label: "Final disposition", value: profile.final_disposition_date }
  ];
  return (
    <div className="prose printable-report">
      <div className="panel-header">
        <p><strong>{profile.case_number}</strong> | {profile.county ?? "Unavailable"} | Initial judicial officer: {profile.judicial_officer_initial ?? "Unavailable"}</p>
        <button className="secondary" onClick={() => window.print()}>Print timeline</button>
      </div>
      <div className="metrics compact-metrics">
        <MetricCard label="Outcome" value={labelize(profile.outcome_bucket)} note="Case-level bucket" />
        <MetricCard label="Primary level" value={profile.primary_charge_level ?? "Unavailable"} note={labelize(profile.primary_offense_category)} />
        <MetricCard label="Filed date" value={shortDate(profile.filed_date)} note="Case-level date" />
        <MetricCard label="Final disposition" value={shortDate(profile.final_disposition_date)} note="May extend past filing window" />
        <MetricCard label="Charges" value={numberOrDash(profile.charge_count)} note="Charge rows" />
        <MetricCard label="Docket events" value={numberOrDash(profile.event_count)} note={`${numberOrDash(profile.hearing_count)} hearings`} />
        <MetricCard label="Revocation activity" value={yesNo(profile.revocation_ever)} note="Case-level flag" />
        <MetricCard label="Warrant activity" value={yesNo(profile.warrant_any_ever)} note={`${numberOrDash(profile.bench_warrant_count)} bench warrants`} />
      </div>
      <h2>Lifecycle</h2>
      <div className="chips">{lifecycle.map((item) => <span key={item.label}>{item.label}: {shortDate(item.value)}</span>)}</div>
      {quality.length ? (
        <>
          <h2>Data Quality Notes</h2>
          <div className="chips">{quality.map((issue, index) => <span key={index}>{String(issue.severity)}: {String(issue.issue)}</span>)}</div>
        </>
      ) : null}
      {profileWarnings.length ? (
        <>
          <h2>Profile Warnings</h2>
          <div className="chips">{profileWarnings.map((warning) => <span key={warning}>{warning}</span>)}</div>
        </>
      ) : null}
      <h2>Charges</h2>
      <div className="table-wrap compact">
        <table>
          <thead><tr><th>#</th><th>Description</th><th>Statute</th><th>Level</th><th>Offense date</th></tr></thead>
          <tbody>
            {charges.map((charge, index) => (
              <tr key={`${charge.case_number}-${charge.charge_number ?? index}`}>
                <td>{charge.charge_number ?? index + 1}</td>
                <td>{charge.charge_description ?? "Unavailable"}</td>
                <td>{charge.statute ?? "Unavailable"}</td>
                <td>{charge.level ?? "Unavailable"}</td>
                <td>{shortDate(charge.offense_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Procedural Timeline</h2>
      <p>Shows API-backed event rows when available, otherwise high-signal local event highlights. Event detail appears only when loaded from the case event API and obvious DOB markers are redacted in public responses.</p>
      <div className="table-wrap compact">
        <table>
          <thead><tr><th>Year</th><th>Date</th><th>Seq.</th><th>Title</th><th>Bucket</th><th>Judge</th><th>Flags</th><th>Linked sentence events</th><th>Detail</th></tr></thead>
          <tbody>
            {events.map((event, index) => (
              <tr key={`${event.event_date}-${event.event_sequence}-${index}`}>
                <td>{event.event_date?.slice(0, 4) ?? "Unavailable"}</td>
                <td>{shortDate(event.event_date)}</td>
                <td>{event.event_sequence ?? ""}</td>
                <td>{event.event_title ?? "Unavailable"}</td>
                <td>{labelize(event.event_type_bucket)}</td>
                <td>{event.judicial_officer ?? "Unavailable"}</td>
                <td>
                  {[event.is_judgment ? "judgment" : "", event.is_plea ? "plea" : "", event.is_revocation ? "revocation" : "", event.is_warrant_any ? "warrant" : "", event.is_dismissal ? "dismissal" : "", event.is_hearing ? "hearing" : "", event.is_motion ? "motion" : "", event.is_order ? "order" : "", event.is_probation_event ? "probation" : "", event.is_service_document ? "service" : "", event.is_bond_event ? "bond" : ""].filter(Boolean).join(", ") || "None"}
                </td>
                <td>{numberOrDash(event.linked_sentence_events ?? 0)}</td>
                <td>{event.event_detail ? <details><summary>Event detail</summary><p>{event.event_detail}</p></details> : "Unavailable"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Financial Display</h2>
      <div className="notice">Attorney-only preview. Bundled financial fields are case-level export values and are not an official payoff quote.</div>
      {financial ? (
        <>
          <div className="metrics compact-metrics">
            <MetricCard label="Balance due" value={dollars(financial.balance_due)} note={balanceStatusLabel(financial.balance_status)} />
            <MetricCard label="Total assessment" value={dollars(financial.total_financial_assessment)} note="Bundled case-profile field" />
            <MetricCard label="Payments and credits" value={dollars(financial.total_payments_and_credits)} note="Bundled case-profile field" />
            <MetricCard label="Reconciliation" value={reconciliationIssue(financial) ? "Review" : "OK"} note={financial.reconciliation_difference === null ? "Unavailable" : `Difference ${dollars(financial.reconciliation_difference)}`} />
          </div>
          <p>Null financial values are displayed as unavailable, not as zero. Financial-related condition text remains separate from these bundled totals unless a future parser explicitly extracts dollar amounts.</p>
        </>
      ) : (
        <p>Financial values are unavailable for this case in the attorney-preview snapshot.</p>
      )}
      <p>Source file: {profile.source_file ?? "Unavailable"}</p>
    </div>
  );
}

function highlight(text: string, query: string) {
  if (!query.trim()) return text;
  const lower = text.toLowerCase();
  const needle = query.toLowerCase();
  const index = lower.indexOf(needle);
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

function EventDetail({ payload, query }: { payload: Record<string, unknown>; query: string }) {
  const event = payload.event as Record<string, unknown>;
  const conditions = payload.conditions as Array<Record<string, unknown>>;
  const flags = payload.flags as Array<Record<string, unknown>>;
  const rawText = String(event.conditions_raw ?? "Unavailable");
  return (
    <div className="prose">
      <p><strong>{String(event.case_number)}</strong> | {String(event.sentence_event_date ?? "Unavailable")} | {String(event.sentence_event_title ?? "Unavailable")}</p>
      <p>Source file: {String(event.source_file ?? "Unavailable")}</p>
      <div className="chips">
        {flags.filter((flag) => flag.flag_value === 1).map((flag) => <span key={String(flag.flag_name)}>{String(flag.flag_name)}</span>)}
      </div>
      <h2>Parsed Conditions</h2>
      <div className="table-wrap compact">
        <table>
          <thead><tr><th>#</th><th>Title</th><th>Status</th><th>Dates</th><th>Comment</th></tr></thead>
          <tbody>
            {conditions.map((condition) => (
              <tr key={String(condition.condition_id)}>
                <td>{String(condition.sequence)}</td>
                <td>{String(condition.title ?? "Unavailable")}</td>
                <td>{String(condition.status ?? "Unavailable")}</td>
                <td>{String(condition.start_date ?? "Unavailable")} to {String(condition.end_date ?? "Unavailable")}</td>
                <td>{String(condition.comment ?? "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>Raw Condition Text</h2>
      <p className="raw-text">{highlight(rawText, query)}</p>
    </div>
  );
}

function FinancialPreviewSection({
  financial,
  filters,
  filteredRows,
  counties,
  judges,
  categories,
  levels,
  updateFilter,
  clearFilters
}: {
  financial: FinancialPreview;
  filters: FinancialFilters;
  filteredRows: FinancialCaseRow[];
  counties: string[];
  judges: string[];
  categories: string[];
  levels: string[];
  updateFilter: <K extends keyof FinancialFilters>(key: K, value: FinancialFilters[K]) => void;
  clearFilters: () => void;
}) {
  const [showFinancialColumns, setShowFinancialColumns] = useState(true);
  const completeness = financial.summary.caseProfileRows
    ? `${financial.summary.availableCases.toLocaleString()} of ${financial.summary.caseProfileRows.toLocaleString()} case-profile rows include at least one bundled financial value; ${financial.summary.unavailableCases.toLocaleString()} are unavailable.`
    : "Financial completeness unavailable.";
  const paidShare = financial.summary.availableCases ? (100 * financial.summary.paidInFullCases / financial.summary.availableCases) : 0;
  const openShare = financial.summary.availableCases ? (100 * financial.summary.openBalanceCases / financial.summary.availableCases) : 0;

  return (
    <section className="page-grid financial-preview">
      <article className="panel prose chart-grid-wide">
        <h2>Financial Display</h2>
        <div className="notice">Attorney-only preview. These case-level financial fields are visible for testing on the beta deployment and are not approved for public release.</div>
        <p>{financial.meta.source}. Values are export-time case data, not an official payoff quote or legal collection advice.</p>
        <p>{completeness}</p>
      </article>

      <section className="metrics">
        <MetricCard label="Total assessment" value={dollars(financial.summary.totalFinancialAssessment)} note="Available case-level dollars" />
        <MetricCard label="Payments and credits" value={dollars(financial.summary.totalPaymentsAndCredits)} note="Available case-level dollars" />
        <MetricCard label="Balance due" value={dollars(financial.summary.totalBalanceDue)} note="Available case-level dollars" />
        <MetricCard label="Open balance cases" value={numberOrDash(financial.summary.openBalanceCases)} note={`${openShare.toFixed(1)}% of available cases`} />
        <MetricCard label="Paid or zero balance" value={numberOrDash(financial.summary.paidInFullCases)} note={`${paidShare.toFixed(1)}% of available cases`} />
        <MetricCard label="Unavailable financials" value={numberOrDash(financial.summary.unavailableCases)} note="Null is not zero" />
        <MetricCard label="Median balance due" value={dollars(financial.summary.medianBalanceDue)} note="Nulls excluded" />
        <MetricCard label="Median assessment" value={dollars(financial.summary.medianFinancialAssessment)} note="Nulls excluded" />
        <MetricCard label="95th percentile balance" value={dollars(financial.summary.p95BalanceDue)} note="Outlier-resistant review" />
        <MetricCard label="Reconciliation issues" value={numberOrDash(financial.summary.reconciliationIssueCases)} note="Assessment - credits != balance" />
      </section>

      <section className="chart-grid">
        <article className="panel">
          <h2>Balance Due Distribution, Dollars</h2>
          <p className="chart-note">Case counts by bundled balance due. Median {dollars(financial.summary.medianBalanceDue)} and 95th percentile {dollars(financial.summary.p95BalanceDue)} are shown in the evidence note so averages do not dominate interpretation.</p>
          <Plot title="financial-balance-distribution" description="Balance due bucket distribution including unavailable rows." data={[{ type: "bar", x: financial.charts.balanceDistribution.map((row) => row.label), y: financial.charts.balanceDistribution.map((row) => row.cases), marker: { color: colors.financial }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ margin: { l: 86, r: 24, t: 20, b: 110 }, xaxis: { title: { text: "Balance due bucket (dollars)", standoff: 14 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, annotations: [{ xref: "paper", yref: "paper", x: 0.98, y: 0.96, xanchor: "right", text: `Median ${dollars(financial.summary.medianBalanceDue)} | P95 ${dollars(financial.summary.p95BalanceDue)}`, showarrow: false, font: { size: 11, color: colors.neutral } }] }} />
          <ChartTable rows={financial.charts.balanceDistribution} barColumn="cases" color={colors.financial} />
          <ChartEvidence unit="case profiles by balance bucket" denominator={`${financial.summary.caseProfileRows.toLocaleString()} case-profile rows, including unavailable`} source={financial.meta.source} caveat="Null financial rows are unavailable, not zero" />
        </article>
        <article className="panel">
          <h2>Total Assessment Distribution, Dollars</h2>
          <p className="chart-note">Case counts by bundled total financial assessment. Null financial rows are shown as unavailable.</p>
          <Plot title="financial-assessment-distribution" description="Total assessment bucket distribution." data={[{ type: "bar", x: financial.charts.assessmentDistribution.map((row) => row.label), y: financial.charts.assessmentDistribution.map((row) => row.cases), marker: { color: colors.sentence }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ margin: { l: 86, r: 24, t: 20, b: 110 }, xaxis: { title: { text: "Total assessment bucket (dollars)", standoff: 14 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, annotations: [{ xref: "paper", yref: "paper", x: 0.98, y: 0.96, xanchor: "right", text: `Median ${dollars(financial.summary.medianFinancialAssessment)}`, showarrow: false, font: { size: 11, color: colors.neutral } }] }} />
          <ChartTable rows={financial.charts.assessmentDistribution} barColumn="cases" color={colors.sentence} />
        </article>
        <article className="panel">
          <h2>Payments And Credits Distribution, Dollars</h2>
          <p className="chart-note">Case counts by bundled total payments and credits. Null financial rows are shown as unavailable.</p>
          <Plot title="financial-payments-distribution" description="Payments and credits bucket distribution." data={[{ type: "bar", x: financial.charts.paymentsDistribution.map((row) => row.label), y: financial.charts.paymentsDistribution.map((row) => row.cases), marker: { color: colors.condition }, hovertemplate: "%{x}<br>%{y:,} cases<extra></extra>" }]} layout={{ margin: { l: 86, r: 24, t: 20, b: 110 }, xaxis: { title: { text: "Payments and credits bucket (dollars)", standoff: 14 }, tickangle: -25, automargin: true }, yaxis: { title: { text: "Cases", standoff: 14 }, automargin: true } }} />
          <ChartTable rows={financial.charts.paymentsDistribution} barColumn="cases" color={colors.condition} />
        </article>
        <article className="panel">
          <h2>Paid, Open, And Unavailable Share</h2>
          <p className="chart-note">Derived from bundled balance and assessment fields. Unavailable means all three bundled financial fields are null.</p>
          <Plot title="financial-status" data={[{ type: "bar", orientation: "h", y: financial.charts.balanceStatus.map((row) => row.label).reverse(), x: financial.charts.balanceStatus.map((row) => row.cases).reverse(), marker: { color: "#b45309" }, hovertemplate: "%{y}<br>%{x:,} cases<extra></extra>" }]} layout={{ margin: { l: 230, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Cases", standoff: 14 }, automargin: true }, yaxis: { automargin: true } }} />
          <ChartTable rows={financial.charts.balanceStatus} />
        </article>
        <article className="panel">
          <h2>Payments/Credits Vs Assessment, Dollars</h2>
          <p className="chart-note">Each point is one case with bundled financial values. Extreme values are retained in the chart and listed in the outlier table.</p>
          <Plot title="financial-payments-assessment-scatter" description="Scatter plot comparing payments and credits against assessment with y equals x reference line." data={[
            { type: "scattergl", mode: "markers", name: "Cases", x: financial.charts.scatter.map((row) => row.total_financial_assessment), y: financial.charts.scatter.map((row) => row.total_payments_and_credits), text: financial.charts.scatter.map((row) => row.case_number), marker: { color: colors.sentence, opacity: 0.38 }, hovertemplate: "%{text}<br>Assessment: $%{x:,.2f}<br>Payments/credits: $%{y:,.2f}<extra></extra>" },
            { type: "scatter", mode: "markers+text", name: "High balance outliers", x: financial.charts.outliers.slice(0, 6).map((row) => row.total_financial_assessment), y: financial.charts.outliers.slice(0, 6).map((row) => row.total_payments_and_credits), text: financial.charts.outliers.slice(0, 6).map((row) => row.case_number), textposition: "top center", marker: { color: colors.warning, size: 8 }, hovertemplate: "%{text}<br>Assessment: $%{x:,.2f}<br>Payments/credits: $%{y:,.2f}<extra></extra>" }
          ]} layout={{ margin: { l: 104, r: 24, t: 20, b: 96 }, xaxis: { title: { text: "Total assessment (dollars)", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Payments and credits (dollars)", standoff: 16 }, automargin: true }, shapes: [{ type: "line", x0: 0, y0: 0, x1: financial.summary.maxFinancialAssessment ?? 1, y1: financial.summary.maxFinancialAssessment ?? 1, line: { color: colors.reference, dash: "dot", width: 2 } }], annotations: [{ xref: "paper", yref: "paper", x: 0.03, y: 0.96, text: "dotted line: payments = assessment", showarrow: false, font: { size: 11, color: colors.neutral } }] }} />
          <ChartTable rows={financial.charts.scatter.slice(0, 50).map((row) => ({ case: row.case_number, assessment: row.total_financial_assessment, payments: row.total_payments_and_credits, balance: row.balance_due }))} />
        </article>
        <article className="panel">
          <h2>Balance Vs Sentence Length</h2>
          <p className="chart-note">Case-level balance compared with max linked confinement and probation values where available. Sentence values are months; balance is dollars.</p>
          <Plot title="financial-balance-sentence-scatter" data={[
            { type: "scattergl", mode: "markers", name: "Confinement months", x: financial.charts.scatter.map((row) => row.confinement_months_imposed), y: financial.charts.scatter.map((row) => row.balance_due), text: financial.charts.scatter.map((row) => row.case_number), marker: { color: "#047857", opacity: 0.45 }, hovertemplate: "%{text}<br>Confinement: %{x} months<br>Balance: $%{y:,.2f}<extra></extra>" },
            { type: "scattergl", mode: "markers", name: "Probation months", x: financial.charts.scatter.map((row) => row.probation_months), y: financial.charts.scatter.map((row) => row.balance_due), text: financial.charts.scatter.map((row) => row.case_number), marker: { color: "#9333ea", opacity: 0.35 }, hovertemplate: "%{text}<br>Probation: %{x} months<br>Balance: $%{y:,.2f}<extra></extra>" }
          ]} layout={{ margin: { l: 104, r: 24, t: 20, b: 96 }, xaxis: { title: { text: "Sentence length (months)", standoff: 14 }, automargin: true }, yaxis: { title: { text: "Balance due (dollars)", standoff: 16 }, automargin: true }, legend: { orientation: "h" } }} />
          <ChartTable rows={financial.charts.scatter.slice(0, 50).map((row) => ({ case: row.case_number, balance: row.balance_due, confinementMonths: row.confinement_months_imposed, probationMonths: row.probation_months }))} />
        </article>
        <article className="panel">
          <h2>Average Financials Over Time, Dollars</h2>
          <p className="chart-note">Monthly averages by final disposition month when available, otherwise filed month. Null financial rows are excluded from dollar averages.</p>
          <Plot title="financials-over-time" data={[
            { type: "scatter", mode: "lines+markers", name: "Avg assessment", x: financial.charts.monthlyFinancials.map((row) => row.month), y: financial.charts.monthlyFinancials.map((row) => row.avgAssessment), line: { color: "#2563eb" }, hovertemplate: "%{x}<br>Avg assessment: $%{y:,.2f}<extra></extra>" },
            { type: "scatter", mode: "lines+markers", name: "Avg balance", x: financial.charts.monthlyFinancials.map((row) => row.month), y: financial.charts.monthlyFinancials.map((row) => row.avgBalanceDue), line: { color: "#b45309" }, hovertemplate: "%{x}<br>Avg balance: $%{y:,.2f}<extra></extra>" }
          ]} layout={{ yaxis: { title: { text: "Average dollars", standoff: 14 }, automargin: true }, xaxis: { title: { text: "Disposition/filed month", standoff: 14 }, automargin: true }, legend: { orientation: "h" } }} />
          <ChartTable rows={financial.charts.monthlyFinancials} />
        </article>
        <article className="panel">
          <h2>Financials By Offense Category</h2>
          <p className="chart-note">Median and 95th percentile balance due by primary offense category. Counts show cases with available balance values.</p>
          <Plot title="financials-by-offense-category" description="Dot plot of median and 95th percentile balance by offense category." data={[
            { type: "scatter", mode: "markers", name: "Median balance", y: financial.charts.offenseDistributionStats.map((row) => labelize(row.label)).reverse(), x: financial.charts.offenseDistributionStats.map((row) => row.medianBalanceDue ?? 0).reverse(), marker: { color: colors.financial, size: 9 }, hovertemplate: "%{y}<br>Median balance: $%{x:,.2f}<extra></extra>" },
            { type: "scatter", mode: "markers", name: "P95 balance", y: financial.charts.offenseDistributionStats.map((row) => labelize(row.label)).reverse(), x: financial.charts.offenseDistributionStats.map((row) => row.p95BalanceDue ?? 0).reverse(), marker: { color: colors.warning, size: 9, symbol: "diamond" }, hovertemplate: "%{y}<br>P95 balance: $%{x:,.2f}<extra></extra>" }
          ]} layout={{ margin: { l: 230, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Balance due distribution (dollars)", standoff: 14 }, automargin: true }, yaxis: { automargin: true }, legend: { orientation: "h" } }} />
          <ChartTable rows={financial.charts.offenseDistributionStats.map((row) => ({ category: labelize(row.label), available: row.availableCases, medianBalanceDue: row.medianBalanceDue, p95BalanceDue: row.p95BalanceDue, medianAssessment: row.medianAssessment }))} columns={["category", "available", "medianBalanceDue", "p95BalanceDue", "medianAssessment"]} barColumn="available" color={colors.financial} />
        </article>
        <article className="panel">
          <h2>Judge Financial Context</h2>
          <p className="chart-note">Initial judicial officer summaries are case-mix context only. Values are not judge causation or performance measures. Table includes median and 95th percentile values so averages do not dominate review.</p>
          <Plot title="financials-by-judge" description="Judge financial context dot plot using median and 95th percentile values." data={[
            { type: "scatter", mode: "markers", name: "Median assessment", y: financial.charts.judgeDistributionStats.slice(0, 18).map((row) => row.label).reverse(), x: financial.charts.judgeDistributionStats.slice(0, 18).map((row) => row.medianAssessment ?? 0).reverse(), marker: { color: colors.judge, size: 9 }, hovertemplate: "%{y}<br>Median assessment: $%{x:,.2f}<extra></extra>" },
            { type: "scatter", mode: "markers", name: "P95 assessment", y: financial.charts.judgeDistributionStats.slice(0, 18).map((row) => row.label).reverse(), x: financial.charts.judgeDistributionStats.slice(0, 18).map((row) => row.p95Assessment ?? 0).reverse(), marker: { color: colors.warning, size: 9, symbol: "diamond" }, hovertemplate: "%{y}<br>P95 assessment: $%{x:,.2f}<extra></extra>" }
          ]} layout={{ margin: { l: 240, r: 24, t: 20, b: 74 }, xaxis: { title: { text: "Assessment distribution (dollars)", standoff: 14 }, automargin: true }, yaxis: { automargin: true }, legend: { orientation: "h" } }} />
          <ChartTable rows={financial.charts.byJudge.map((row) => {
            const stats = financial.charts.judgeDistributionStats.find((stat) => stat.label === row.label);
            return { judge: row.label, cases: row.cases, available: row.availableCases, avgBalanceDue: row.avgBalanceDue, medianBalanceDue: stats?.medianBalanceDue, p95BalanceDue: stats?.p95BalanceDue, avgAssessment: row.avgAssessment, medianAssessment: stats?.medianAssessment };
          })} columns={["judge", "cases", "available", "medianBalanceDue", "p95BalanceDue", "medianAssessment"]} barColumn="available" color={colors.judge} />
        </article>
        <article className="panel chart-grid-wide">
          <h2>Financial Case Search</h2>
          <p className="chart-note">{filteredRows.length.toLocaleString()} matching cases. Export includes attorney-preview financial columns. Public case search remains financial-free.</p>
          <div className="inline-form financial-filters">
            <label>Search<input id="financial-query" name="financial-query" value={filters.query} onChange={(event) => updateFilter("query", event.target.value)} placeholder="Case, county, judge, category" /></label>
            <label>Status<select id="financial-status" name="financial-status" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}><option value="">Any</option><option value="open_balance">Open balance</option><option value="paid_in_full">Paid in full / zero balance</option><option value="unavailable">Unavailable</option><option value="review">Review</option></select></label>
            <label>County<select id="financial-county" name="financial-county" value={filters.county} onChange={(event) => updateFilter("county", event.target.value)}><option value="">Any</option>{counties.map((county) => <option key={county} value={county}>{county}</option>)}</select></label>
            <label>Judge<select id="financial-judge" name="financial-judge" value={filters.judge} onChange={(event) => updateFilter("judge", event.target.value)}><option value="">Any</option>{judges.map((judge) => <option key={judge} value={judge}>{judge}</option>)}</select></label>
            <label>Offense category<select id="financial-offense-category" name="financial-offense-category" value={filters.offenseCategory} onChange={(event) => updateFilter("offenseCategory", event.target.value)}><option value="">Any</option>{categories.map((category) => <option key={category} value={category}>{labelize(category)}</option>)}</select></label>
            <label>Charge level<select id="financial-charge-level" name="financial-charge-level" value={filters.chargeLevel} onChange={(event) => updateFilter("chargeLevel", event.target.value)}><option value="">Any</option>{levels.map((level) => <option key={level} value={level}>{level}</option>)}</select></label>
            <label>Revocation<select id="financial-revocation" name="financial-revocation" value={filters.revocation} onChange={(event) => updateFilter("revocation", event.target.value)}><option value="">Any</option><option value="1">Yes</option><option value="0">No</option></select></label>
            <label>Min balance<input id="financial-min-balance" name="financial-min-balance" type="number" min="0" value={filters.minBalance} onChange={(event) => updateFilter("minBalance", event.target.value)} /></label>
            <label>Max balance<input id="financial-max-balance" name="financial-max-balance" type="number" min="0" value={filters.maxBalance} onChange={(event) => updateFilter("maxBalance", event.target.value)} /></label>
            <label>Min assessment<input id="financial-min-assessment" name="financial-min-assessment" type="number" min="0" value={filters.minAssessment} onChange={(event) => updateFilter("minAssessment", event.target.value)} /></label>
            <label>Max assessment<input id="financial-max-assessment" name="financial-max-assessment" type="number" min="0" value={filters.maxAssessment} onChange={(event) => updateFilter("maxAssessment", event.target.value)} /></label>
            <label>Min payments<input id="financial-min-payments" name="financial-min-payments" type="number" min="0" value={filters.minPayments} onChange={(event) => updateFilter("minPayments", event.target.value)} /></label>
            <label>Max payments<input id="financial-max-payments" name="financial-max-payments" type="number" min="0" value={filters.maxPayments} onChange={(event) => updateFilter("maxPayments", event.target.value)} /></label>
            <label>Quality<select id="financial-quality" name="financial-quality" value={filters.quality} onChange={(event) => updateFilter("quality", event.target.value)}><option value="">Any</option><option value="discrepancy">Reconciliation issue</option><option value="payments_exceed">Payments exceed assessment</option><option value="high_balance">High balance ($25k+)</option></select></label>
            <label>Sort<select id="financial-sort" name="financial-sort" value={filters.sort} onChange={(event) => updateFilter("sort", event.target.value)}><option value="balance_desc">Balance due</option><option value="assessment_desc">Assessment</option><option value="payments_desc">Payments/credits</option><option value="filed_desc">Newest filed</option><option value="judge">Judge</option><option value="case">Case number</option></select></label>
            <label>Financial columns<select id="financial-column-visibility" name="financial-column-visibility" value={showFinancialColumns ? "show" : "hide"} onChange={(event) => setShowFinancialColumns(event.target.value === "show")}><option value="show">Show</option><option value="hide">Hide</option></select></label>
            <button className="secondary" onClick={clearFilters}>Clear filters</button>
            <button onClick={() => exportFinancialCsv(filteredRows)}><Download aria-hidden="true" /> Export attorney financial CSV</button>
          </div>
          <div className="table-wrap compact">
            <table>
              <thead><tr><th>Case</th><th>Filed</th><th>County</th><th>Judge</th><th>Category</th><th>Status</th>{showFinancialColumns ? <><th>Balance due</th><th>Assessment</th><th>Payments/credits</th><th>Review</th></> : null}</tr></thead>
              <tbody>
                {filteredRows.slice(0, 150).map((row) => (
                  <tr key={row.case_number}>
                    <td>{row.case_number}</td>
                    <td>{shortDate(row.filed_date)}</td>
                    <td>{row.county ?? "Unavailable"}</td>
                    <td>{row.judicial_officer_initial ?? "Unavailable"}</td>
                    <td>{labelize(row.primary_offense_category)}</td>
                    <td>{balanceStatusLabel(row.balance_status)}</td>
                    {showFinancialColumns ? (
                      <>
                        <td>{dollars(row.balance_due)}</td>
                        <td>{dollars(row.total_financial_assessment)}</td>
                        <td>{dollars(row.total_payments_and_credits)}</td>
                        <td>{reconciliationIssue(row) ? `Difference ${dollars(row.reconciliation_difference)}` : "OK"}</td>
                      </>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
        <article className="panel">
          <h2>Highest Balance Due Cases</h2>
          <p className="chart-note">Outlier audit table so capped or bucketed charts do not hide high-dollar cases.</p>
          <ChartTable rows={financial.charts.outliers.map((row) => ({ case: row.case_number, county: row.county, judge: row.judicial_officer_initial, balance: row.balance_due, assessment: row.total_financial_assessment, payments: row.total_payments_and_credits }))} />
        </article>
        <article className="panel">
          <h2>Financial Data Quality</h2>
          <p className="chart-note">Completeness and reconciliation checks for the bundled financial fields.</p>
          <div className="metrics compact-metrics">
            <MetricCard label="Negative value cases" value={numberOrDash(financial.summary.negativeValueCases)} note="Any bundled financial field" />
            <MetricCard label="Payments exceed assessment" value={numberOrDash(financial.summary.paymentsExceedAssessmentCases)} note="Audit review table available" />
            <MetricCard label="Zero balance cases" value={numberOrDash(financial.summary.zeroBalanceCases)} note="May be paid or no balance" />
            <MetricCard label="Zero assessment cases" value={numberOrDash(financial.summary.zeroAssessmentCases)} note="Assessment field equals zero" />
          </div>
          <ChartTable rows={financial.charts.discrepancies.map((row) => ({ case: row.case_number, balance: row.balance_due, assessment: row.total_financial_assessment, payments: row.total_payments_and_credits, difference: row.difference }))} />
        </article>
        <article className="panel">
          <h2>Financial Condition Text Links</h2>
          <p className="chart-note">Condition titles whose text mentions fines, fees, restitution, civil judgment, court costs, or payment plans. These are text links only and are not added to bundled totals.</p>
          <ChartTable rows={financial.charts.conditionFinancialLinks.map((row) => ({ condition: row.conditionTitle ?? "Unavailable", "sentence events": row.sentenceEvents, cases: row.cases }))} />
        </article>
      </section>
    </section>
  );
}
